import { useState, useRef, useEffect } from "react";
import { Sparkles, Sun, Flame, User, ChevronLeft, ChevronRight, Mic, MicOff, Square, Lock, Star, Clock, Crown, RotateCcw, Video, CheckCircle, BarChart2, Heart, Briefcase, Sprout, Home, Activity, Moon, Coins, Globe } from "lucide-react";
import { supabase } from "./supabase";
import OneSignal from "react-onesignal";
import NativeOneSignal from "@onesignal/capacitor-plugin";
import { Browser } from "@capacitor/browser";
import { App as CapApp } from "@capacitor/app";

const gold = "#c9a84c";
const purple = "#7c3aed";

const MAJOR = [
  { id:"m00", name:"Il Matto", type:"major", img:"MW_The_Fool.webp", meaning_up:"Nuovi inizi, innocenza, avventura, libertà", meaning_rev:"Imprudenza, rischio, ingenuità" },
  { id:"m01", name:"Il Mago", type:"major", img:"MW_The_Magician.webp", meaning_up:"Volontà, abilità, concentrazione, azione", meaning_rev:"Manipolazione, scarsa pianificazione" },
  { id:"m02", name:"La Papessa", type:"major", img:"MW_The_High_Priestess.webp", meaning_up:"Intuizione, mistero, saggezza interiore", meaning_rev:"Segreti nascosti, disconnessione" },
  { id:"m03", name:"L'Imperatrice", type:"major", img:"MW_The_Empress.webp", meaning_up:"Fertilità, abbondanza, natura, creatività", meaning_rev:"Dipendenza, soffocamento" },
  { id:"m04", name:"L'Imperatore", type:"major", img:"MW_The_Emperor.webp", meaning_up:"Autorità, struttura, stabilità, controllo", meaning_rev:"Rigidità, dominazione" },
  { id:"m05", name:"Il Papa", type:"major", img:"MW_The_Hierophant.webp", meaning_up:"Tradizione, spiritualità, guida morale", meaning_rev:"Conformismo, dogma, ipocrisia" },
  { id:"m06", name:"Gli Amanti", type:"major", img:"MW_The_Lovers.webp", meaning_up:"Amore, armonia, scelte importanti", meaning_rev:"Squilibrio, scelte sbagliate" },
  { id:"m07", name:"Il Carro", type:"major", img:"MW_The_Chariot.webp", meaning_up:"Controllo, vittoria, determinazione", meaning_rev:"Mancanza di direzione, aggressività" },
  { id:"m08", name:"La Forza", type:"major", img:"MW_Strength.webp", meaning_up:"Coraggio, pazienza, controllo interiore", meaning_rev:"Debolezza, dubbio di sé" },
  { id:"m09", name:"L'Eremita", type:"major", img:"MW_The_Hermit.webp", meaning_up:"Riflessione, solitudine, ricerca interiore", meaning_rev:"Isolamento, ritiro eccessivo" },
  { id:"m10", name:"La Ruota della Fortuna", type:"major", img:"MW_Wheel_of_Fortune.webp", meaning_up:"Cambiamento, cicli, fortuna", meaning_rev:"Cattiva fortuna, resistenza al cambiamento" },
  { id:"m11", name:"La Giustizia", type:"major", img:"MW_Justice.webp", meaning_up:"Equità, verità, causa ed effetto", meaning_rev:"Ingiustizia, disonestà" },
  { id:"m12", name:"L'Appeso", type:"major", img:"MW_The_Hanged_man.webp", meaning_up:"Pausa, rinuncia, nuove prospettive", meaning_rev:"Ritardi, resistenza, martirio" },
  { id:"m13", name:"La Morte", type:"major", img:"MW_Death.webp", meaning_up:"Trasformazione, fine e inizio, transizione", meaning_rev:"Resistenza al cambiamento, stagnazione" },
  { id:"m14", name:"La Temperanza", type:"major", img:"MW_Temperance.webp", meaning_up:"Equilibrio, moderazione, pazienza", meaning_rev:"Eccesso, squilibrio, fretta" },
  { id:"m15", name:"Il Diavolo", type:"major", img:"MW_The_Devil.webp", meaning_up:"Ombre, dipendenza, materialismo", meaning_rev:"Liberazione, rottura delle catene" },
  { id:"m16", name:"La Torre", type:"major", img:"MW_The_Tower.webp", meaning_up:"Rivelazione improvvisa, caos, cambiamento radicale", meaning_rev:"Prevenire il disastro, paura del cambiamento" },
  { id:"m17", name:"La Stella", type:"major", img:"MW_The_Star.webp", meaning_up:"Speranza, fede, rinnovamento, ispirazione", meaning_rev:"Disperazione, scoraggiamento, sfiducia" },
  { id:"m18", name:"La Luna", type:"major", img:"MW_The_Moon.webp", meaning_up:"Illusione, paura, inconscio, sogni", meaning_rev:"Confusione, paure liberate, chiarezza" },
  { id:"m19", name:"Il Sole", type:"major", img:"MW_The_Sun.webp", meaning_up:"Gioia, successo, vitalità, chiarezza", meaning_rev:"Pessimismo, tristezza, blocco" },
  { id:"m20", name:"Il Giudizio", type:"major", img:"MW_Judgement.webp", meaning_up:"Rinascita, chiamata interiore, assoluzione", meaning_rev:"Dubbio di sé, rifiuto del cambiamento" },
  { id:"m21", name:"Il Mondo", type:"major", img:"MW_The_World.webp", meaning_up:"Completamento, integrazione, realizzazione", meaning_rev:"Incompletezza, stagnazione, rimandi" },
];

const MINOR_IMGS = {
  cups:["MW_Ace_of_Cups","MW_Two_of_Cups","MW_Three_of_Cups","MW_Four_of_Cups","MW_Five_of_Cups","MW_Six_of_Cups","MW_Seven_of_Cups","MW_Eight_of_Cups","MW_Nine_of_Cups","MW_Ten_of_Cups","MW_Page_of_Cups","MW_Knight_of_Cups","MW_Queen_of_Cups","MW_King_of_Cups"],
  pentacles:["MW_Ace_of_Pentacles","MW_Two_of_Pentacles","MW_Three_of_Pentacles","MW_Four_of_Pentacles","MW_Five_of_Pentacles","MW_Six_of_Pentacles","MW_Seven_of_Pentacles","MW_Eight_of_Pentacles","MW_Nine_of_Pentacles","MW_Ten_of_Pentacles","MW_Page_of_Pentacles","MW_Knight_of_Pentacles","MW_Queen_of_Pentacles","MW_King_of_Pentacles"],
  swords:["MW_Ace_of_Swords","MW_Two_of_Swords","MW_Three_of_Swords","MW_Four_of_Swords","MW_Five_of_Swords","MW_Six_of_Swords","MW_Seven_of_Swords","MW_Eight_of_Swords","MW_Nine_of_Swords","MW_Ten_of_Swords","MW_Page_of_Swords","MW_Knight_of_Swords","MW_Queen_of_Swords","MW_King_of_Swords"],
  wands:["MW_Ace_of_Wands","MW_Two_of_Wands","MW_Three_of_Wands","MW_Four_of_Wands","MW_Five_of_Wands","MW_Six_of_Wands","MW_Seven_of_Wands","MW_Eight_of_Wands","MW_Nine_of_Wands","MW_Ten_of_Wands","MW_Page_of_Wands","MW_Knight_of_Wands","MW_Queen_of_Wands","MW_King_of_Wands"],
};

function makeMinor(suit, suitName, meanings) {
  const names = ["Asso","Due","Tre","Quattro","Cinque","Sei","Sette","Otto","Nove","Dieci","Fante","Cavaliere","Regina","Re"];
  return names.map(function(n,i){ return { id:suit+String(i+1).padStart(2,"0"), name:n+" di "+suitName, type:"minor", suit, img:MINOR_IMGS[suit][i]+".webp", meaning_up:meanings[i][0], meaning_rev:meanings[i][1] }; });
}

const CUPS = makeMinor("cups","Coppe",[["Nuovo amore, intuizione, offerta emotiva","Blocco emotivo, offerta rifiutata"],["Unione, partnership, connessione reciproca","Squilibrio nella relazione, rottura"],["Celebrazione, amicizia, creatività","Eccessi, superficialità, indulgenza"],["Contemplazione, insoddisfazione, apatia","Ritiro, ritirarsi in sé, nuova consapevolezza"],["Perdita, rimpianto, focalizzarsi sul negativo","Accettazione, andare avanti, perdonare"],["Nostalgia, infanzia, ricordi felici","Vivere nel passato, ingenuità, irrealtà"],["Illusioni, scelte, fantasia","Chiarezza, scelte realistiche, determinazione"],["Abbandono, allontanamento, cercare di meglio","Stagnazione, paura di lasciare andare"],["Appagamento emotivo, felicità, soddisfazione","Superficialità, mancanza di realizzazione"],["Gioia duratura, armonia familiare, sogni realizzati","Dolore interiore, perfezione irraggiungibile"],["Creatività, sensibilità, messaggi d'amore","Immaturità emotiva, delusione"],["Romanticismo, charme, proposta sentimentale","Inaffidabilità, manipolazione emotiva"],["Compassione, creatività, intuizione profonda","Dipendenza emotiva, umore instabile"],["Saggezza emotiva, calma, equilibrio interiore","Manipolazione, freddezza emotiva"]]);
const PENTACLES = makeMinor("pentacles","Denari",[["Nuova opportunità finanziaria, prosperità","Perdita finanziaria, opportunità mancata"],["Equilibrio finanziario, adattabilità","Squilibrio, disorganizzazione, debiti"],["Lavoro di squadra, abilità artigianale","Mediocrità, mancanza di impegno"],["Risparmio, conservatorismo, possesso","Avarizia, blocco, paura di perdere"],["Difficoltà finanziarie, insicurezza","Recupero, miglioramento, nuova stabilità"],["Generosità, dono, condivisione","Debiti, egoismo, sfruttamento"],["Pazienza, investimento a lungo termine","Impazienza, investimenti sbagliati"],["Abilità pratiche, diligenza","Lavoro eccessivo, mancanza di ambizione"],["Abbondanza, prosperità guadagnata","Dipendenza materiale, perdita di guadagni"],["Ricchezza, eredità, stabilità familiare","Perdita finanziaria, conflitti familiari"],["Diligenza, affidabilità, studiosità","Pigrizia, immaturità nelle finanze"],["Ambizione, determinazione, lavoro concreto","Impazienza, mancanza di direzione"],["Benessere materiale, generosità","Squilibrio lavoro-vita, materialismo"],["Successo materiale, leadership pratica","Corruzione, avidità, rigidità"]]);
const SWORDS = makeMinor("swords","Spade",[["Chiarezza mentale, verità, nuovi inizi","Confusione, crudeltà, distruzione"],["Stallo, difficoltà a scegliere","Menzogna a sé stessi, confusione"],["Dolore, lutto, separazione, tradimento","Guarigione, perdono, recupero"],["Riposo, recupero, contemplazione","Esaurimento, stress"],["Conflitto, sconfitta, meschinità","Riconciliazione, pace raggiunta"],["Transizione, cambiamento, lasciare andare","Resistenza al cambiamento"],["Inganno, furto, astuzia","Essere scoperti, conseguenze"],["Limitazione, paura, prigione mentale","Liberazione, nuovo coraggio"],["Ansia, preoccupazione, pensieri negativi","Liberazione dall'ansia, chiarezza"],["Tragedia, perdita devastante","Recupero, resistenza"],["Comunicazione acuta, intelligenza","Superficialità, parole sbagliate"],["Decisione rapida, logica, ambizione","Impulsività, aggressività verbale"],["Intelletto acuto, indipendenza","Frialdezza emotiva, crudeltà"],["Autorità intellettuale, chiarezza","Tirannia, abuso di potere"]]);
const WANDS = makeMinor("wands","Bastoni",[["Nuova avventura, ispirazione, potenziale","Ritardi, mancanza di motivazione"],["Pianificazione futura, progresso","Impazienza, mancanza di pianificazione"],["Collaborazione, espansione","Conflitti, ostacoli nella crescita"],["Celebrazione, armonia, traguardo","Mancanza di armonia, instabilità"],["Competizione, tensione, conflitto","Evitare il conflitto, debolezza"],["Vittoria pubblica, riconoscimento","Caduta pubblica, mancanza di supporto"],["Perseveranza, difesa, coraggio","Esaurimento, cedere alle pressioni"],["Velocità, azione rapida, notizie","Ritardi, notizie cattive"],["Determinazione, resistenza","Esaurimento, crollo"],["Fardello, responsabilità pesante","Liberarsi dai pesi, delegare"],["Energia, avventura, entusiasmo","Impulsività, energia mal diretta"],["Passione, avventura romantica","Instabilità, comportamento avventato"],["Indipendenza, visione, determinazione","Impulsività, testardaggine"],["Leadership carismatica, visione","Prepotenza, impulsività, egotismo"]]);

const ALL_CARDS = [...MAJOR,...CUPS,...PENTACLES,...SWORDS,...WANDS];
const POSITIONS = ["Passato","Presente","Futuro"];
const FOCUS_OPTIONS = [
  {k:"amore",l:"Amore",icon:Heart},{k:"lavoro",l:"Lavoro",icon:Briefcase},
  {k:"crescita",l:"Crescita personale",icon:Sprout},{k:"famiglia",l:"Famiglia",icon:Home},
  {k:"salute",l:"Salute",icon:Activity},{k:"spiritualita",l:"Spiritualità",icon:Moon},
  {k:"denaro",l:"Denaro",icon:Coins},{k:"tutto",l:"Un po' di tutto",icon:Globe},
];

// ── Percorso ─────────────────────────────────────────────────
const LUNAR_STAGES = [
  { days:1, name:"Giorno 1", subtitle:"Dove sei ora" },
  { days:2, name:"Giorno 2", subtitle:"Cosa non stai vedendo" },
  { days:3, name:"Giorno 3", subtitle:"Cosa ti blocca" },
  { days:4, name:"Giorno 4", subtitle:"Cosa chiede attenzione" },
  { days:5, name:"Giorno 5", subtitle:"Che direzione prendere" },
  { days:6, name:"Giorno 6", subtitle:"Cosa lasciare andare" },
  { days:7, name:"Giorno 7", subtitle:"Cosa integrare" },
];

function getStoredData() {
  try { const d = localStorage.getItem("rosemary_tarot"); return d ? JSON.parse(d) : { consultations:[], lastFreeDate:null, isPremium:false, streak:0, lastStreakDate:null, dailyCardDate:null, onboardingDone:false, journeyQuestion:null, journeyReadings:{}, journeyStartDate:null, journeyOpenedDates:{}, journeyCompletedOnce:false }; }
  catch { return { consultations:[], lastFreeDate:null, isPremium:false, streak:0, lastStreakDate:null, dailyCardDate:null, onboardingDone:false, journeyQuestion:null, journeyReadings:{}, journeyStartDate:null, journeyOpenedDates:{}, journeyCompletedOnce:false }; }
}
function saveData(d) { try { localStorage.setItem("rosemary_tarot", JSON.stringify(d)); } catch {} }
// Deve restare allineata al calcolo del server (Europe/Rome): se il client usa
// UTC, tra mezzanotte e le 02:00 le due date divergono e il free tier si rompe.
function romeISODate(d) { return (d || new Date()).toLocaleDateString("en-CA", { timeZone: "Europe/Rome" }); }
function pickCards(pool) { return [...pool].sort(function(){return Math.random()-0.5;}).slice(0,3).map(function(c){return {...c,reversed:Math.random()>0.5};}); }
function pickOne(pool) { var c=pool[Math.floor(Math.random()*pool.length)]; return {...c,reversed:Math.random()>0.5}; }

async function transcribeAudio(blob) {
  var form = new FormData(); form.append("audio",blob,"audio.webm");
  var res = await fetch("https://api.rosemarytarot.com/api/transcribe",{method:"POST",body:form});
  var data = await res.json(); if(!data.text) throw new Error("err"); return data.text;
}
async function fetchNarrative(question,cards,token) {
  var headers = {"Content-Type":"application/json"};
  if(token) headers["Authorization"] = "Bearer "+token;
  var res = await fetch("https://api.rosemarytarot.com/api/narrative",{method:"POST",headers,body:JSON.stringify({question,cards:cards.map(function(c,i){return {name:c.name,position:POSITIONS[i],reversed:c.reversed,meaning:c.reversed?c.meaning_rev:c.meaning_up};})})});
  var data = await res.json(); if(!res.ok){var e=new Error(data.error||"Errore");e.status=res.status;throw e;} return data.narrative;
}

async function fetchJourneyReading(stage, cards, journeyQuestion, previousNarrative) {
  var res = await fetch("https://api.rosemarytarot.com/api/journey-reading", {
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body: JSON.stringify({
      stage: { days: stage.days, name: stage.name, subtitle: stage.subtitle },
      cards: cards.map(function(c){ return { meaning: c.reversed ? c.meaning_rev : c.meaning_up, reversed: c.reversed }; }),
      journeyQuestion,
      previousNarrative: previousNarrative || null,
    })
  });
  var data = await res.json();
  return data.narrative || "Le stelle stanno elaborando il tuo percorso.";
}

async function fetchStreakReading(cards, days) {
  var res = await fetch("https://api.rosemarytarot.com/api/streak-reading", {
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body: JSON.stringify({
      days,
      cards: cards.map(function(c){ return {meaning: c.reversed ? c.meaning_rev : c.meaning_up, reversed: c.reversed}; })
    })
  });
  var data = await res.json();
  return data.narrative || "Le stelle stanno elaborando il tuo percorso.";
}

async function fetchDailyNarrative(card) {
  var meaning = card.reversed ? card.meaning_rev : card.meaning_up;
  var mainTheme = meaning.split(",")[0].trim();
  var res = await fetch("https://api.rosemarytarot.com/api/daily",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({theme:mainTheme})});
  var data = await res.json();
  return data.narrative || "Le stelle hanno un messaggio speciale per te oggi.";
}

function Stars() {
  var stars = Array.from({length:80},function(_,i){return {id:i,x:Math.random()*100,y:Math.random()*100,size:Math.random()*2+0.5,delay:Math.random()*3,dur:2+Math.random()*3};});
  return (
    <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:0}}>
      <div style={{position:"absolute",inset:0,overflow:"hidden"}}>
        <div style={{position:"absolute",width:"60vw",height:"60vw",borderRadius:"50%",background:"radial-gradient(circle,rgba(120,40,180,0.22) 0%,transparent 70%)",top:"-10%",left:"-10%",animation:"nebula1 12s ease-in-out infinite alternate,pulse1 8s ease-in-out infinite alternate",filter:"blur(18px)"}}/>
        <div style={{position:"absolute",width:"50vw",height:"50vw",borderRadius:"50%",background:"radial-gradient(circle,rgba(30,80,180,0.18) 0%,transparent 70%)",top:"30%",right:"-15%",animation:"nebula2 15s ease-in-out infinite alternate,pulse2 10s ease-in-out infinite alternate",filter:"blur(22px)"}}/>
        <div style={{position:"absolute",width:"70vw",height:"40vw",borderRadius:"50%",background:"radial-gradient(circle,rgba(180,40,100,0.14) 0%,transparent 70%)",bottom:"-10%",left:"20%",animation:"nebula3 18s ease-in-out infinite alternate,pulse3 13s ease-in-out infinite alternate",filter:"blur(25px)"}}/>
        <div style={{position:"absolute",width:"40vw",height:"40vw",borderRadius:"50%",background:"radial-gradient(circle,rgba(40,160,180,0.12) 0%,transparent 70%)",top:"10%",right:"20%",animation:"nebula1 20s ease-in-out infinite alternate-reverse,pulse1 9s ease-in-out infinite alternate-reverse",filter:"blur(20px)"}}/>
        <div style={{position:"absolute",width:"35vw",height:"35vw",borderRadius:"50%",background:"radial-gradient(circle,rgba(200,160,40,0.08) 0%,transparent 70%)",top:"50%",left:"30%",animation:"nebula2 25s ease-in-out infinite alternate,pulse2 11s ease-in-out infinite alternate",filter:"blur(30px)"}}/>
      </div>
      <svg width="100%" height="100%" style={{position:"absolute"}}>
        {stars.map(function(s){return (
          <circle key={s.id} cx={s.x+"%"} cy={s.y+"%"} r={s.size} fill={s.id%5===0?"#c0d8ff":"#f0d080"} opacity="0.5">
            <animate attributeName="opacity" values="0.1;0.9;0.1" dur={s.dur+"s"} begin={s.delay+"s"} repeatCount="indefinite"/>
          </circle>
        );})}
      </svg>
    </div>
  );
}

function RosemaryImg(props) {
  var size=props.size||180; var img=props.img||"rosemary-tarot_main.webp";
  return <img src={"/images/"+img} alt="Rosemary" style={{width:size,height:"auto",objectFit:"contain",filter:"drop-shadow(0 0 24px rgba(201,168,76,0.4))",flexShrink:0}}/>;
}

function CardPlaceholder(props) {
  var card=props.card,flipped=props.flipped,reversed=props.reversed,position=props.position,onClick=props.onClick,small=props.small;
  var sz=small?80:100;
  var suitColors={major:gold,cups:"#6a9fd8",pentacles:"#7ab87a",swords:"#c0c0d0",wands:"#d4845a"};
  var col=card?(suitColors[card.suit]||suitColors.major):gold;
  return (
    <div onClick={onClick} style={{cursor:onClick?"pointer":"default",display:"flex",flexDirection:"column",alignItems:"center",gap:6,flex:1}}>
      {position&&<div style={{fontSize:10,letterSpacing:2,color:gold,textTransform:"uppercase"}}>{position}</div>}
      <div style={{width:"100%",maxWidth:sz,aspectRatio:"2/3.4",borderRadius:8,border:"2px solid "+(flipped?col:gold),background:flipped?"linear-gradient(135deg,#2a1a4e,#1a0a2e)":"linear-gradient(135deg,#4a2a8e,#2a1a6e)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:flipped?"0 0 20px "+col+"55":"0 0 20px rgba(124,58,237,0.4)",transition:"all 0.6s ease",transform:reversed?"rotate(180deg)":"none",position:"relative",overflow:"hidden"}}>
        {flipped
          ? <img src={"/cards/"+card.img} alt={card.name} className="card-flip" style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:6}}/>
          : <><div style={{textAlign:"center",padding:8}}><Sun size={small?20:28} color="rgba(201,168,76,0.5)" strokeWidth={1}/></div><div style={{position:"absolute",inset:0,background:"radial-gradient(circle at 50% 40%,rgba(124,58,237,0.2),transparent 70%)",pointerEvents:"none"}}/></>
        }
      </div>
      {flipped&&<div style={{fontSize:10,color:col,textAlign:"center",maxWidth:sz}}>{card.name}</div>}
    </div>
  );
}

function LoadingOrb() {
  return <div style={{textAlign:"center",padding:"20px 0"}}><div style={{fontSize:36,animation:"pulse 1.5s infinite"}}>🔮</div><p style={{color:gold,fontSize:13,marginTop:10,fontStyle:"italic"}}>Rosemary sta leggendo le stelle…</p></div>;
}

function MoonIcon({phase, size=24, color=gold}) {
  // Simple moon phase SVG icons
  const icons = {
    "Luna Nuova":    <circle cx={size/2} cy={size/2} r={size/2-2} fill="none" stroke={color} strokeWidth={1.5}/>,
    "Crescente":     <path d={`M${size/2} 2 A${size/2-2} ${size/2-2} 0 1 1 ${size/2} ${size-2} A${size/3} ${size/2-2} 0 1 0 ${size/2} 2`} fill={color} opacity={0.8}/>,
    "Primo Quarto":  <><circle cx={size/2} cy={size/2} r={size/2-2} fill="none" stroke={color} strokeWidth={1.5}/><rect x={size/2} y={2} width={size/2-2} height={size-4} fill={color} opacity={0.8}/></>,
    "Gibbosa":       <path d={`M${size/2} 2 A${size/2-2} ${size/2-2} 0 1 1 ${size/2} ${size-2} A${size/4} ${size/2-2} 0 1 1 ${size/2} 2`} fill={color} opacity={0.8}/>,
    "Luna Piena":    <circle cx={size/2} cy={size/2} r={size/2-2} fill={color} opacity={0.9}/>,
    "Calante":       <path d={`M${size/2} 2 A${size/4} ${size/2-2} 0 1 0 ${size/2} ${size-2} A${size/2-2} ${size/2-2} 0 1 0 ${size/2} 2`} fill={color} opacity={0.8}/>,
    "Balsamica":     <path d={`M${size/2} 2 A${size/2-2} ${size/2-2} 0 1 0 ${size/2} ${size-2} A${size/3} ${size/2-2} 0 1 1 ${size/2} 2`} fill={color} opacity={0.8}/>,
  };
  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>{icons[phase]||icons["Luna Nuova"]}</svg>;
}

function DoubleHeart({size=24, color=gold, strokeWidth=1.5}) {
  var h = Math.round(size*0.72);
  return (
    <span style={{position:"relative",display:"inline-block",width:size,height:size,flexShrink:0}}>
      <Heart size={h} color={color} strokeWidth={strokeWidth} style={{position:"absolute",left:0,top:0,opacity:0.5}}/>
      <Heart size={h} color={color} strokeWidth={strokeWidth} style={{position:"absolute",right:0,bottom:0}}/>
    </span>
  );
}

function BottomNav(props) {
  var active=props.active,onNav=props.onNav,badges=props.badges;
  var tabs=[{key:"consult",label:"Consulta",icon:Sparkles},{key:"daily",label:"Oggi",icon:Sun},{key:"streak",label:"Percorso",icon:Moon},{key:"profile",label:"Profilo",icon:User}];
  return (
    <div style={{position:"fixed",bottom:0,left:0,right:0,zIndex:100,background:"rgba(10,5,25,0.95)",borderTop:"1px solid rgba(201,168,76,0.2)",backdropFilter:"blur(12px)",display:"flex",justifyContent:"space-around",padding:"10px 0 16px"}}>
      {tabs.map(function(t){
        var isActive=active===t.key; var badge=badges[t.key]; var Icon=t.icon;
        return (
          <button key={t.key} onClick={function(){onNav(t.key);}} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,background:"none",border:"none",cursor:"pointer",padding:"4px 12px",position:"relative"}}>
            <div style={{position:"relative"}}>
              <Icon size={22} color={isActive?gold:"#6a5a7a"} strokeWidth={isActive?2:1.5}/>
              {badge&&<div style={{position:"absolute",top:-4,right:-6,width:8,height:8,borderRadius:"50%",background:"#e05050",border:"1.5px solid rgba(10,5,25,0.95)"}}/>}
            </div>
            <span style={{fontSize:10,color:isActive?gold:"#6a5a7a",letterSpacing:0.5,fontWeight:isActive?600:400}}>{t.label}</span>
            {isActive&&<div style={{position:"absolute",bottom:-10,left:"50%",transform:"translateX(-50%)",width:4,height:4,borderRadius:"50%",background:gold}}/>}
          </button>
        );
      })}
    </div>
  );
}

export default function App() {
  var [tab,setTab] = useState("consult");
  var [screen,setScreen] = useState("home");
  var [session,setSession] = useState(null);
  var [authLoading,setAuthLoading] = useState(true);
  var [authMode,setAuthMode] = useState("login"); // "login" | "register" | "reset"
  var [authEmail,setAuthEmail] = useState("");
  var [authPassword,setAuthPassword] = useState("");
  var [authPasswordConfirm,setAuthPasswordConfirm] = useState("");
  var [authError,setAuthError] = useState("");
  var [authLoading2,setAuthLoading2] = useState(false);
  var [resetSent,setResetSent] = useState(false);
  var [question,setQuestion] = useState("");
  var [deckMode,setDeckMode] = useState(null);
  var [drawnCards,setDrawnCards] = useState([]);
  var [flipped,setFlipped] = useState([false,false,false]);
  var [selectedCard,setSelectedCard] = useState(null);
  var [data,setData] = useState(getStoredData);
  var [animating,setAnimating] = useState(false);
  var [narrative,setNarrative] = useState("");
  var [loadingNarrative,setLoadingNarrative] = useState(false);
  var [invalidQuestion,setInvalidQuestion] = useState(false);
  var [validating,setValidating] = useState(false);
  var [recording,setRecording] = useState(false);
  var [transcribing,setTranscribing] = useState(false);
  var [showTranscriptEdit,setShowTranscriptEdit] = useState(false);
  var [dailyCard,setDailyCard] = useState(null);
  var [dailyNarrative,setDailyNarrative] = useState("");
  var [loadingDaily,setLoadingDaily] = useState(false);
  var [dailyRevealed,setDailyRevealed] = useState(false);
  var [dailyCardPicked,setDailyCardPicked] = useState(null);
  var [expandedConsultation,setExpandedConsultation] = useState(null);
  var [showOnboarding,setShowOnboarding] = useState(!getStoredData().onboardingDone);
  var [onboardingStep,setOnboardingStep] = useState(0);
  var [ob,setOb] = useState({name:"",gender:"",day:"",month:"",year:"",focus:""});
  var [marketingConsent,setMarketingConsent] = useState(false);
  var [showMarketingPopup,setShowMarketingPopup] = useState(false);

  // Percorso state
  var [journeyReadings,setJourneyReadings] = useState(data.journeyReadings||{});
  var [journeyOpenedDates,setJourneyOpenedDates] = useState(data.journeyOpenedDates||{});
  var [loadingJourneyStage,setLoadingJourneyStage] = useState({});
  var [activeJourneyStage,setActiveJourneyStage] = useState(null);
  var [journeyQuestion,setJourneyQuestion] = useState(data.journeyQuestion||null);
  var [journeyQuestionDraft,setJourneyQuestionDraft] = useState(data.journeyQuestion||"");
  var [showJourneySetup,setShowJourneySetup] = useState(false);
  var [paymentSuccess,setPaymentSuccess] = useState(false);
  var [loadingCheckout,setLoadingCheckout] = useState(null);
  var [loadingPortal,setLoadingPortal] = useState(false);
  // Lettura combinata
  var [realReadings,setRealReadings] = useState([]);
  var [realQuestion,setRealQuestion] = useState("");
  var [loadingRealCheckout,setLoadingRealCheckout] = useState(false);
  var [realOrderSuccess,setRealOrderSuccess] = useState(false);

  var mediaRecorderRef = useRef(null);
  var chunksRef = useRef([]);
  var questionRef = useRef("");

  var today = new Date().toDateString();
  var todayISO = romeISODate();
  var canUseFree = data.lastFreeDate !== todayISO;
  var canConsult = data.isPremium || canUseFree;
  var dailyCardSeen = data.dailyCardDate === today || data.dailyCardDate === todayISO;
  var badges = {consult:false,daily:!dailyCardSeen,streak:false,profile:false};

  var journeyStartDate = data.journeyStartDate || null;
  var journeyLocked = !!journeyReadings[LUNAR_STAGES[0].days];
  // Il primo percorso completo è gratis; per avviarne un altro serve Premium.
  var journeyCompletedOnce = !!data.journeyCompletedOnce;
  var canStartNewJourney = !journeyCompletedOnce || data.isPremium;
  function isStageUnlocked(stage, idx) {
    if (!journeyStartDate) return false;
    if (idx === 0) return true;
    var prev = LUNAR_STAGES[idx - 1];
    var prevOpened = journeyOpenedDates[prev.days];
    return !!prevOpened && todayISO > prevOpened;
  }
  var currentStageIndex = LUNAR_STAGES.findIndex(function(s,i){ return !isStageUnlocked(s,i); });
  if(currentStageIndex === -1) currentStageIndex = LUNAR_STAGES.length;

  useEffect(function(){
    supabase.auth.getSession().then(function(ref){
      setSession(ref.data.session);
      setAuthLoading(false);
      if(ref.data.session) loadUserData(ref.data.session.user.id);
    });
    var sub = supabase.auth.onAuthStateChange(function(_event,s){
      setSession(s);
      if(s) loadUserData(s.user.id);
    });
    return function(){sub.data.subscription.unsubscribe();};
  },[]);

  // Inizializzazione OneSignal
  useEffect(function(){
    if(window.Capacitor && window.Capacitor.isNativePlatform()) {
      NativeOneSignal.initialize(process.env.REACT_APP_ONESIGNAL_APP_ID);
      NativeOneSignal.Notifications.requestPermission(false);
    } else {
      OneSignal.init({
        appId: process.env.REACT_APP_ONESIGNAL_APP_ID,
        allowLocalhostAsSecureOrigin: true,
        notifyButton: { enable: false },
      }).then(function(){
        OneSignal.Slidedown.promptPush();
      }).catch(function(err){
        console.warn("OneSignal init error:", err);
      });
    }
  },[]);

  // Collega l'utente Supabase a OneSignal quando fa login
  useEffect(function(){
    if(!session) return;
    if(window.Capacitor && window.Capacitor.isNativePlatform()) {
      NativeOneSignal.login(session.user.id).catch(function(){});
    } else {
      OneSignal.login(session.user.id).catch(function(){});
    }
  },[session]);

  // Tasto indietro Android
  useEffect(function(){
    if(!window.Capacitor || !window.Capacitor.isNativePlatform()) return;
    var handler = CapApp.addListener("backButton", function(){
      if(screen !== "home") {
        if(screen === "drawing" || screen === "reading" || screen === "question") setScreen("home");
        else if(screen === "premium") setScreen("home");
        else setScreen("home");
      } else if(tab !== "consult") {
        setTab("consult");
        setScreen("home");
      }
    });
    return function(){ handler.then(function(h){ h.remove(); }); };
  },[screen, tab]);
  useEffect(function(){
    if(!window.Capacitor || !window.Capacitor.isNativePlatform()) return;
    var handleUrl = function(data){
      var url = data.url;
      if(!url || !url.startsWith("com.rosemarytarot.app://")) return;

      // Supabase passa i token nel fragment (#) o come query params
      var fragment = url.includes("#") ? url.split("#")[1] : "";
      var query = url.includes("?") ? url.split("?")[1].split("#")[0] : "";
      var params = new URLSearchParams(fragment || query);

      var accessToken = params.get("access_token");
      var refreshToken = params.get("refresh_token");

      if(accessToken && refreshToken){
        supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        }).then(function(ref){
          if(ref.data.session){
            loadUserData(ref.data.session.user.id);
          }
        });
      } else {
        // Fallback — ricarica la sessione
        supabase.auth.getSession().then(function(ref){
          if(ref.data.session) loadUserData(ref.data.session.user.id);
        });
      }
    };
    var listenerPromise = CapApp.addListener("appUrlOpen", handleUrl);
    return function(){ listenerPromise.then(function(l){ l.remove(); }); };
  },[]);

  // Gestione ritorno da Stripe
  useEffect(function(){
    var params = new URLSearchParams(window.location.search);
    var payment = params.get("payment");
    if(payment === "success"){
      window.history.replaceState({}, "", window.location.pathname);
      if(session) loadUserData(session.user.id);
      setTab("consult");
      setScreen("home");
      setPaymentSuccess(true);
      setTimeout(function(){ setPaymentSuccess(false); }, 5000);
    }
    if(payment === "cancelled"){
      window.history.replaceState({}, "", window.location.pathname);
    }
    var reading = params.get("reading");
    if(reading === "success"){
      window.history.replaceState({}, "", window.location.pathname);
      if(session) loadRealReadings(session.user.id);
      setRealQuestion("");
      setTab("consult");
      setScreen("real");
      setRealOrderSuccess(true);
      setTimeout(function(){ setRealOrderSuccess(false); }, 6000);
    }
    if(reading === "cancelled"){
      window.history.replaceState({}, "", window.location.pathname);
    }
  },[session]);

  async function loadUserData(userId){
    var {data:profile} = await supabase
      .from("profiles")
      .select("*")
      .eq("id",userId)
      .single();

    if(profile){
      var {data:consultations} = await supabase
        .from("consultations")
        .select("*")
        .eq("user_id",userId)
        .order("created_at",{ascending:false})
        .limit(10);

      // Load journey readings from Supabase
      var {data:journeyRows} = await supabase
        .from("journey_readings")
        .select("*")
        .eq("user_id",userId);

      var loadedJourneyReadings = {};
      if(journeyRows){
        journeyRows.forEach(function(r){ loadedJourneyReadings[r.stage_days] = r.narrative; });
      }

      var merged = {
        ...getStoredData(),
        isPremium: profile.is_premium && (!profile.premium_expires_at || new Date(profile.premium_expires_at) > new Date()),
        streak: profile.streak||0,
        lastStreakDate: profile.last_streak_date||null,
        dailyCardDate: profile.daily_card_date||null,
        dailyCardPicked: profile.daily_card||null,
        dailyNarrative: profile.daily_narrative||null,
        userName: profile.name||getStoredData().userName||null,
        userGender: profile.gender||getStoredData().userGender||null,
        userBirthdate: profile.birthdate||getStoredData().userBirthdate||null,
        userFocus: profile.focus||getStoredData().userFocus||null,
        onboardingDone: !!(profile.name)||getStoredData().onboardingDone||false,
        journeyQuestion: profile.journey_question||getStoredData().journeyQuestion||null,
        journeyReadings: loadedJourneyReadings,
        journeyStartDate: profile.journey_start_date||getStoredData().journeyStartDate||null,
        journeyOpenedDates: profile.journey_opened_dates||getStoredData().journeyOpenedDates||{},
        journeyCompletedOnce: profile.journey_completed_once||getStoredData().journeyCompletedOnce||false,
        lastFreeDate: profile.last_free_date||getStoredData().lastFreeDate||null,
        consultations: consultations ? consultations.map(function(c){return {date:new Date(c.created_at).toDateString(),question:c.question,cards:c.cards,deckMode:c.deck_mode,narrative:c.narrative};}) : getStoredData().consultations,
      };
      setData(merged);
      saveData(merged);
      setJourneyReadings(loadedJourneyReadings);
      setJourneyOpenedDates(merged.journeyOpenedDates||{});
      loadRealReadings(userId);
      setJourneyQuestion(merged.journeyQuestion);
      setJourneyQuestionDraft(merged.journeyQuestion||"");
      setShowOnboarding(!merged.onboardingDone);
      if(merged.onboardingDone && (profile.email_marketing_consent === null || profile.email_marketing_consent === false) && !localStorage.getItem("rt_marketing_popup_shown")){
        setShowMarketingPopup(true);
      }
    } else {
      var local = getStoredData();
      await supabase.from("profiles").upsert({
        id: userId,
        name: local.userName||null,
        gender: local.userGender||null,
        birthdate: local.userBirthdate||null,
        focus: local.userFocus||null,
        streak: local.streak||0,
        last_streak_date: local.lastStreakDate||null,
        daily_card_date: local.dailyCardDate||null,
        daily_card: local.dailyCardPicked||null,
        daily_narrative: local.dailyNarrative||null,
        journey_question: local.journeyQuestion||null,
        journey_start_date: local.journeyStartDate||null,
        journey_opened_dates: local.journeyOpenedDates||{},
        journey_completed_once: local.journeyCompletedOnce||false,
      });
    }
  }

  async function saveUserData(userId, updates){
    await supabase.from("profiles").update(updates).eq("id",userId);
  }

  async function saveConsultation(userId, consultation){
    await supabase.from("consultations").insert({
      user_id: userId,
      question: consultation.question,
      cards: consultation.cards,
      narrative: consultation.narrative||null,
      deck_mode: consultation.deckMode||null,
    });
  }

  async function signInWithGoogle(){
    var redirectTo = window.Capacitor && window.Capacitor.isNativePlatform()
      ? "com.rosemarytarot.app://login-callback"
      : window.location.origin;

    if(window.Capacitor && window.Capacitor.isNativePlatform()){
      // Su app nativa usa il browser in-app per OAuth
      var {data, error} = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo,
          skipBrowserRedirect: true,
        }
      });
      if(data?.url){
        await Browser.open({ url: data.url, windowName: "_self" });
      }
    } else {
      supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo }
      });
    }
  }

  function signOut(){ supabase.auth.signOut(); }

  async function handleLogin(){
    if(!authEmail.trim()||!authPassword.trim()) return;
    setAuthLoading2(true); setAuthError("");
    var {error} = await supabase.auth.signInWithPassword({email:authEmail.trim(),password:authPassword});
    if(error){ setAuthError("Email o password non corretti."); }
    setAuthLoading2(false);
  }

  async function handleRegister(){
    if(!authEmail.trim()||!authPassword.trim()) return;
    if(authPassword !== authPasswordConfirm){ setAuthError("Le password non coincidono."); return; }
    if(authPassword.length < 6){ setAuthError("La password deve essere di almeno 6 caratteri."); return; }
    setAuthLoading2(true); setAuthError("");
    var {error} = await supabase.auth.signUp({email:authEmail.trim(),password:authPassword});
    if(error){ setAuthError("Errore nella registrazione. Riprova."); }
    else { setAuthError(""); setAuthMode("login"); setAuthError("Registrazione completata! Controlla la tua email per confermare l'account."); }
    setAuthLoading2(false);
  }

  async function handleReset(){
    if(!authEmail.trim()) return;
    setAuthLoading2(true); setAuthError("");
    await supabase.auth.resetPasswordForEmail(authEmail.trim(), {redirectTo: "https://app.rosemarytarot.com"});
    setResetSent(true);
    setAuthLoading2(false);
  }

  async function activatePremium(plan){
    if(!session) return;
    setLoadingCheckout(plan);
    try{
      var res = await fetch("https://api.rosemarytarot.com/api/stripe-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, userId: session.user.id, userEmail: session.user.email })
      });
      var d = await res.json();
      if(d.url){
        if(window.Capacitor && window.Capacitor.isNativePlatform()){
          await Browser.open({ url: d.url, presentationStyle: "popover" });
        } else {
          window.location.href = d.url;
        }
      } else {
        alert("Errore nella creazione del pagamento. Riprova.");
      }
    } catch(err){
      console.error("Checkout error:", err);
      alert("Errore di connessione. Riprova.");
    }
    setLoadingCheckout(null);
  }

  async function loadRealReadings(userId){
    try{
      var r = await supabase.from("real_readings").select("*").eq("user_id",userId).order("created_at",{ascending:false});
      if(r.data) setRealReadings(r.data);
    }catch(e){}
  }

  async function startRealReadingCheckout(){
    if(!session) return;
    var q = (realQuestion||"").trim();
    if(q.length < 3) return;
    setLoadingRealCheckout(true);
    try{
      var res = await fetch("https://api.rosemarytarot.com/api/real-reading-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer "+session.access_token },
        body: JSON.stringify({ question: q })
      });
      var d = await res.json();
      if(d.url){
        if(window.Capacitor && window.Capacitor.isNativePlatform()){
          await Browser.open({ url: d.url, presentationStyle: "popover" });
        } else {
          window.location.href = d.url;
        }
      } else {
        alert(d.error||"Errore nella creazione del pagamento. Riprova.");
      }
    } catch(err){
      console.error("Real reading checkout error:", err);
      alert("Errore di connessione. Riprova.");
    }
    setLoadingRealCheckout(false);
  }

  async function openStripePortal(){
    if(!session) return;
    setLoadingPortal(true);
    try{
      var res = await fetch("https://api.rosemarytarot.com/api/stripe-portal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: session.user.id })
      });
      var d = await res.json();
      if(d.url){
        if(window.Capacitor && window.Capacitor.isNativePlatform()){
          await Browser.open({ url: d.url, presentationStyle: "popover" });
        } else {
          window.location.href = d.url;
        }
      } else {
        alert("Errore nell'apertura del portale. Riprova.");
      }
    } catch(err){
      console.error("Portal error:", err);
      alert("Errore di connessione. Riprova.");
    }
    setLoadingPortal(false);
  }

  function getPool(){
    if(deckMode==="major") return MAJOR;
    if(deckMode==="minor") return [...CUPS,...PENTACLES,...SWORDS,...WANDS];
    return ALL_CARDS;
  }

  useEffect(function(){
    if(tab==="daily" && !dailyCardPicked){
      setDailyCardPicked(pickOne(MAJOR));
    }
  },[tab]);

  useEffect(function(){
    if(data.dailyCardDate && (data.dailyCardDate===today||data.dailyCardDate===todayISO) && data.dailyCardPicked){
      setDailyCardPicked(data.dailyCardPicked);
      setDailyCard(data.dailyCardPicked);
      setDailyRevealed(true);
      if(data.dailyNarrative) setDailyNarrative(data.dailyNarrative);
    }
  },[data.dailyCardDate, data.dailyCardPicked]);

  function handleNav(key){
    setTab(key);
    if(key==="consult") setScreen("home");
    if(key==="daily") setScreen("daily");
    if(key==="streak") setScreen("streak");
    if(key==="profile") setScreen("profile");
  }

  async function revealDailyCard(){
    if(!dailyCardPicked||dailyRevealed) return;
    setDailyRevealed(true); setDailyCard(dailyCardPicked); setLoadingDaily(true);
    var yesterday=new Date(); yesterday.setDate(yesterday.getDate()-1);
    var yStr=yesterday.toDateString();
    var yISO=romeISODate(yesterday);
    var lastStreak=data.lastStreakDate||"";
    var newStreak=(lastStreak===yStr||lastStreak===yISO)?(data.streak||0)+1:(lastStreak===today||lastStreak===todayISO)?data.streak:1;
    var pickedForSave={img:dailyCardPicked.img,name:dailyCardPicked.name,reversed:dailyCardPicked.reversed,meaning_up:dailyCardPicked.meaning_up,meaning_rev:dailyCardPicked.meaning_rev};
    var nd={...data,dailyCardDate:todayISO,streak:newStreak,lastStreakDate:todayISO,dailyCardPicked:pickedForSave};
    setData(nd); saveData(nd);
    if(session){
      await saveUserData(session.user.id,{streak:newStreak,last_streak_date:todayISO,daily_card_date:todayISO,daily_card:pickedForSave});
      var upsertResult = await supabase.from("daily_cards").upsert({
        user_id: session.user.id,
        card_name: dailyCardPicked.name,
        card_img: dailyCardPicked.img,
        reversed: dailyCardPicked.reversed,
        meaning: dailyCardPicked.reversed ? dailyCardPicked.meaning_rev : dailyCardPicked.meaning_up,
        date: todayISO,
      },{onConflict:"user_id,date"});
      if(upsertResult.error) console.error("daily_cards error:", upsertResult.error);
    }
    var n=await fetchDailyNarrative(dailyCardPicked);
    setDailyNarrative(n);
    var nd2={...nd,dailyNarrative:n}; setData(nd2); saveData(nd2);
    if(session) await saveUserData(session.user.id,{daily_narrative:n});
    setLoadingDaily(false);
  }

  async function startConsultation(overrideQ){
    var finalQ=(overrideQ||question||"").trim();
    if(!finalQ) return;
    setValidating(true); setInvalidQuestion(false);
    try{
      var res=await fetch("https://api.rosemarytarot.com/api/validate",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({question:finalQ})});
      if(res.ok){var d=await res.json();if(!d.valid){setInvalidQuestion(true);setValidating(false);return;}}
    }catch{}
    setValidating(false); setQuestion(finalQ);
    var cards=pickCards(getPool());
    setDrawnCards(cards); setFlipped([false,false,false]); setSelectedCard(null); setNarrative("");
    setScreen("drawing");
  }

  function flipCard(i){
    if(flipped[i]||animating) return;
    setAnimating(true);
    var nf=[...flipped]; nf[i]=true; setFlipped(nf);
    setTimeout(function(){setAnimating(false);},700);
    if(i===2){
      var allCards=drawnCards;
      setTimeout(async function(){
        setLoadingNarrative(true);
        var nd={...data,lastFreeDate:todayISO,consultations:[{date:today,question,cards:allCards,deckMode,narrative:""},...data.consultations.slice(0,9)]};
        setData(nd); saveData(nd);
        try{
          var n=await fetchNarrative(question,allCards,session?.access_token); setNarrative(n);
          var consultation={question,cards:allCards,deckMode,narrative:n};
          var nd2={...nd,consultations:[{date:today,...consultation},...nd.consultations.slice(1)]}; setData(nd2); saveData(nd2);
          if(session) saveConsultation(session.user.id,consultation);
        }catch{setNarrative("Le stelle non riescono a parlare in questo momento. Riprova tra poco.");}
        setLoadingNarrative(false);
      },800);
    }
  }

  function allFlipped(){return flipped.every(Boolean);}

  async function startRecording(){
    try{
      var stream=await navigator.mediaDevices.getUserMedia({audio:true});
      chunksRef.current=[];
      var mr=new MediaRecorder(stream); mediaRecorderRef.current=mr;
      mr.ondataavailable=function(e){if(e.data.size>0)chunksRef.current.push(e.data);};
      mr.onstop=async function(){
        stream.getTracks().forEach(function(t){t.stop();});
        var blob=new Blob(chunksRef.current,{type:"audio/webm"});
        setTranscribing(true);
        try{
          var text=await transcribeAudio(blob);
          var wordCount = text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
          if(text && text.trim() && wordCount >= 3){
            var t=text.trim();
            questionRef.current=t;
            setQuestion(t);
            setShowTranscriptEdit(true);
          } else {
            // Trascrizione vuota o troppo breve
            setQuestion("");
            setInvalidQuestion(true);
          }
        }catch{
          setQuestion("");
        }
        setTranscribing(false);
      };
      mr.start(); setRecording(true);
    }catch{alert("Impossibile accedere al microfono.");}
  }

  function stopRecording(){
    if(mediaRecorderRef.current&&mediaRecorderRef.current.state!=="inactive"){mediaRecorderRef.current.stop();setRecording(false);}
  }

  function completeOnboarding(){
    var birthdate=ob.day&&ob.month&&ob.year?ob.year+"-"+ob.month+"-"+ob.day:"";
    var nd={...data,onboardingDone:true,userName:ob.name||data.userName,userGender:ob.gender||data.userGender,userBirthdate:birthdate||data.userBirthdate,userFocus:ob.focus||data.userFocus};
    setData(nd); saveData(nd); setShowOnboarding(false);
    if(session){
      saveUserData(session.user.id,{
        name:ob.name||data.userName||null,
        gender:ob.gender||data.userGender||null,
        birthdate:birthdate||data.userBirthdate||null,
        focus:ob.focus||data.userFocus||null,
        email_marketing_consent: marketingConsent,
        email_marketing_consent_at: new Date().toISOString(),
      });
    }
  }

  async function saveJourneyQuestion(q){
    var trimmed = (q||"").trim();
    if(!trimmed) return;
    var startDate = data.journeyStartDate || todayISO;
    var nd = {...data, journeyQuestion: trimmed, journeyStartDate: startDate};
    setData(nd); saveData(nd);
    setJourneyQuestion(trimmed);
    setJourneyQuestionDraft(trimmed);
    if(session) await saveUserData(session.user.id, {journey_question: trimmed, journey_start_date: startDate});
  }

  async function resetJourney(){
    var nd = {...data, journeyQuestion:null, journeyStartDate:null, journeyOpenedDates:{}, journeyReadings:{}};
    setData(nd); saveData(nd);
    setJourneyQuestion(null);
    setJourneyQuestionDraft("");
    setJourneyOpenedDates({});
    setJourneyReadings({});
    setActiveJourneyStage(null);
    setShowJourneySetup(true);
    if(session){
      await supabase.from("journey_readings").delete().eq("user_id",session.user.id);
      await saveUserData(session.user.id,{journey_question:null,journey_start_date:null,journey_opened_dates:{}});
    }
  }

  async function unlockJourneyStage(stage){
    // Toggle: if already open, close
    if(activeJourneyStage===stage.days){ setActiveJourneyStage(null); return; }
    setActiveJourneyStage(stage.days);

    // Record opened date on first open (drives next-day unlock)
    var newOpenedDates = {...journeyOpenedDates};
    var completedOnce = data.journeyCompletedOnce || false;
    if(!newOpenedDates[stage.days]){
      newOpenedDates[stage.days] = todayISO;
      setJourneyOpenedDates(newOpenedDates);
      // Primo percorso completato: tutte le 7 tappe aperte almeno una volta.
      if(!completedOnce && Object.keys(newOpenedDates).length >= LUNAR_STAGES.length){
        completedOnce = true;
      }
      var nd0 = {...data, journeyOpenedDates: newOpenedDates, journeyCompletedOnce: completedOnce};
      setData(nd0); saveData(nd0);
      if(session) await saveUserData(session.user.id, {journey_opened_dates: newOpenedDates, journey_completed_once: completedOnce});
    }

    // If narrative already generated, just open — no API call
    if(journeyReadings[stage.days]) return;

    setLoadingJourneyStage(function(prev){return {...prev,[stage.days]:true};});

    // Draw 3 fresh cards from the full deck for this stage
    var stageCards = pickCards(ALL_CARDS);

    var stageIndex = LUNAR_STAGES.findIndex(function(s){return s.days===stage.days;});
    var previousNarrative = stageIndex > 0 ? (journeyReadings[LUNAR_STAGES[stageIndex-1].days]||null) : null;
    var narrative = await fetchJourneyReading(stage, stageCards, journeyQuestion, previousNarrative);

    setJourneyReadings(function(prev){return {...prev,[stage.days]:narrative};});
    var nd = {...data, journeyReadings:{...data.journeyReadings,[stage.days]:narrative}, journeyOpenedDates: newOpenedDates, journeyCompletedOnce: completedOnce};
    setData(nd); saveData(nd);

    await supabase.from("journey_readings").upsert({
      user_id: session.user.id,
      stage_days: stage.days,
      stage_name: stage.name,
      narrative: narrative,
      journey_question: journeyQuestion,
    },{onConflict:"user_id,stage_days"});

    setLoadingJourneyStage(function(prev){return {...prev,[stage.days]:false};});
  }

  var ps={position:"relative",zIndex:1,maxWidth:480,margin:"0 auto",padding:"0 20px 90px"};
  var ss={minHeight:"calc(100svh - 64px)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",textAlign:"center",gap:16,padding:"20px 0 80px"};

  return (
    <div style={{height:"100svh",background:"radial-gradient(ellipse at 20% 50%,#1a0a3e 0%,transparent 50%),radial-gradient(ellipse at 80% 20%,#0d1a3e 0%,transparent 40%),radial-gradient(ellipse at 60% 80%,#2a0a2e 0%,transparent 45%),linear-gradient(135deg,#060410 0%,#0d0820 50%,#060410 100%)",color:"#e8d5b7",fontFamily:"'Mulish',sans-serif",position:"fixed",inset:0,overflowY:"auto",overscrollBehavior:"none"}}>
      <Stars/>

      {paymentSuccess&&(
        <div style={{position:"fixed",top:0,left:0,right:0,zIndex:200,background:"linear-gradient(135deg,rgba(201,168,76,0.97),rgba(160,120,48,0.97))",padding:"14px 20px",display:"flex",alignItems:"center",justifyContent:"center",gap:10,animation:"fadeIn 0.4s ease"}}>
          <Crown size={18} color="#1a0a2e"/>
          <span style={{color:"#1a0a2e",fontWeight:"bold",fontSize:14}}>Benvenuta nel Premium! Le stelle sono tutte per te ✨</span>
        </div>
      )}

      {authLoading&&(
        <div style={{position:"fixed",inset:0,zIndex:300,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <LoadingOrb/>
        </div>
      )}

      {!authLoading&&!session&&(
        <div className="rt-login-scroll" style={{zIndex:200,background:"radial-gradient(ellipse at 20% 50%,#1a0a3e 0%,transparent 50%),radial-gradient(ellipse at 80% 20%,#0d1a3e 0%,transparent 40%),linear-gradient(135deg,#060410 0%,#0d0820 50%,#060410 100%)"}}>
          <div style={{minHeight:"100%",display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"24px 24px 120px"}}>
          <div style={{width:"100%",maxWidth:380,display:"flex",flexDirection:"column",alignItems:"center",gap:20,textAlign:"center",paddingTop:40}}>
            <RosemaryImg size={140}/>
            <h1 style={{fontSize:28,color:gold,letterSpacing:3,margin:0,textShadow:"0 0 20px rgba(201,168,76,0.5)"}}>Rosemary Tarot</h1>
            <p style={{color:"#a09080",fontSize:14,margin:0}}>
              {authMode==="login"?"Accedi al tuo account":authMode==="register"?"Crea il tuo account":"Recupera la password"}
            </p>

            {/* Google login */}
            {authMode!=="reset"&&(
              <div onClick={signInWithGoogle}
                style={{width:"100%",background:"rgba(30,10,60,0.8)",border:"1px solid "+gold,borderRadius:12,padding:"14px 18px",cursor:"pointer",display:"flex",alignItems:"center",gap:14,color:"#e8d5b7",fontSize:15,boxSizing:"border-box"}}>
                <svg width="20" height="20" viewBox="0 0 24 24" style={{flexShrink:0}}><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                <span style={{flex:1,textAlign:"left"}}>Continua con Google</span>
                <ChevronRight size={16} color={gold}/>
              </div>
            )}

            {authMode!=="reset"&&(
              <div style={{display:"flex",alignItems:"center",gap:10,width:"100%"}}>
                <div style={{flex:1,height:1,background:"rgba(255,255,255,0.08)"}}/>
                <span style={{color:"#4a3a6a",fontSize:12}}>oppure</span>
                <div style={{flex:1,height:1,background:"rgba(255,255,255,0.08)"}}/>
              </div>
            )}

            {/* Form email + password */}
            <div style={{width:"100%",display:"flex",flexDirection:"column",gap:10}}>
              <input value={authEmail} onChange={function(e){setAuthEmail(e.target.value);setAuthError("");}}
                placeholder="Email"
                type="email"
                style={{width:"100%",background:"rgba(255,255,255,0.05)",border:"1px solid #3a2a5a",borderRadius:8,color:"#e8d5b7",fontSize:14,padding:"12px 16px",outline:"none",fontFamily:"Mulish,sans-serif",boxSizing:"border-box"}}/>

              {authMode!=="reset"&&(
                <input value={authPassword} onChange={function(e){setAuthPassword(e.target.value);setAuthError("");}}
                  placeholder="Password"
                  type="password"
                  style={{width:"100%",background:"rgba(255,255,255,0.05)",border:"1px solid #3a2a5a",borderRadius:8,color:"#e8d5b7",fontSize:14,padding:"12px 16px",outline:"none",fontFamily:"Mulish,sans-serif",boxSizing:"border-box"}}/>
              )}

              {authMode==="register"&&(
                <input value={authPasswordConfirm} onChange={function(e){setAuthPasswordConfirm(e.target.value);setAuthError("");}}
                  placeholder="Conferma password"
                  type="password"
                  style={{width:"100%",background:"rgba(255,255,255,0.05)",border:"1px solid #3a2a5a",borderRadius:8,color:"#e8d5b7",fontSize:14,padding:"12px 16px",outline:"none",fontFamily:"Mulish,sans-serif",boxSizing:"border-box"}}/>
              )}

              {authError&&(
                <p style={{color: authError.includes("completata") ? "#7ab87a" : "#e8a0a0",fontSize:12,margin:0,textAlign:"left"}}>{authError}</p>
              )}

              {resetSent?(
                <div style={{display:"flex",flexDirection:"column",gap:8,alignItems:"center"}}>
                  <p style={{color:"#7ab87a",fontSize:13,margin:0}}>Email inviata! Controlla la tua casella.</p>
                  <div onClick={function(){setAuthMode("login");setResetSent(false);}} style={{color:"#6a5a7a",cursor:"pointer",fontSize:13}}>← Torna al login</div>
                </div>
              ):(
                <div onClick={authLoading2?undefined:authMode==="login"?handleLogin:authMode==="register"?handleRegister:handleReset}
                  style={{background:authEmail.trim()?"linear-gradient(135deg,"+purple+",#4c1d95)":"#2a1a3e",color:authEmail.trim()?"#fff":"#6a5a7a",border:"1px solid "+(authEmail.trim()?gold:"#3a2a5a"),borderRadius:8,padding:"13px",fontSize:15,cursor:authEmail.trim()&&!authLoading2?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  {authLoading2?<><Clock size={16}/> Attendere…</>:authMode==="login"?<><Sparkles size={15}/> Accedi</>:authMode==="register"?<><Sparkles size={15}/> Registrati</>:<><Sparkles size={15}/> Invia email di reset</>}
                </div>
              )}
            </div>

            {/* Switch tra login/register/reset */}
            <div style={{display:"flex",flexDirection:"column",gap:8,alignItems:"center"}}>
              {authMode==="login"&&(
                <>
                  <div onClick={function(){setAuthMode("register");setAuthError("");}} style={{color:"#c9a84c",cursor:"pointer",fontSize:13}}>
                    Non hai un account? Registrati
                  </div>
                  <div onClick={function(){setAuthMode("reset");setAuthError("");}} style={{color:"#6a5a7a",cursor:"pointer",fontSize:12}}>
                    Password dimenticata?
                  </div>
                </>
              )}
              {authMode==="register"&&(
                <div onClick={function(){setAuthMode("login");setAuthError("");}} style={{color:"#6a5a7a",cursor:"pointer",fontSize:13}}>
                  Hai già un account? Accedi
                </div>
              )}
              {authMode==="reset"&&!resetSent&&(
                <div onClick={function(){setAuthMode("login");setAuthError("");}} style={{color:"#6a5a7a",cursor:"pointer",fontSize:13}}>
                  ← Torna al login
                </div>
              )}
            </div>

          </div>
          </div>
        </div>
      )}

      {!authLoading&&session&&showOnboarding&&(
        <div style={{position:"fixed",inset:0,zIndex:200,background:"rgba(6,4,16,0.97)",display:"flex",alignItems:"center",justifyContent:"center",padding:24,overflowY:"auto"}}>
          <div style={{width:"100%",maxWidth:400,display:"flex",flexDirection:"column",alignItems:"center",gap:20,textAlign:"center",paddingBottom:40}}>
            <RosemaryImg size={140}/>
            <h2 style={{color:gold,fontSize:28,letterSpacing:2,margin:0}}>Piacere di conoscerti</h2>
            <p style={{color:"#a09080",fontSize:14,margin:0}}>Raccontami un po' di te per personalizzare la tua esperienza</p>
            {onboardingStep===0&&(
              <div style={{width:"100%",display:"flex",flexDirection:"column",gap:14}}>
                <div style={{display:"flex",flexDirection:"column",gap:8,textAlign:"left"}}>
                  <label style={{color:gold,fontSize:13,letterSpacing:1}}>Come ti chiami?</label>
                  <input value={ob.name} onChange={function(e){setOb({...ob,name:e.target.value});}} placeholder="Il tuo nome"
                    style={{background:"rgba(255,255,255,0.05)",border:"1px solid "+gold,borderRadius:8,color:"#e8d5b7",fontSize:15,padding:"12px 16px",outline:"none",fontFamily:"Mulish,sans-serif",width:"100%",boxSizing:"border-box",colorScheme:"dark"}}/>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8,textAlign:"left"}}>
                  <label style={{color:gold,fontSize:13,letterSpacing:1}}>Genere</label>
                  <div style={{display:"flex",gap:8}}>
                    {[{k:"f",l:"Donna"},{k:"m",l:"Uomo"},{k:"n",l:"Non specificato"}].map(function(g){
                      var sel=ob.gender===g.k;
                      return <div key={g.k} onClick={function(){setOb({...ob,gender:g.k});}}
                        style={{flex:1,padding:"12px 8px",borderRadius:8,border:"1px solid "+(sel?gold:"#3a2a5a"),background:sel?"rgba(201,168,76,0.15)":"rgba(255,255,255,0.05)",color:sel?"#e8d5b7":"#cabaa2",cursor:"pointer",fontSize:13,textAlign:"center"}}>
                        {g.l}
                      </div>;
                    })}
                  </div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8,textAlign:"left"}}>
                  <label style={{color:gold,fontSize:13,letterSpacing:1}}>Data di nascita</label>
                  <div style={{display:"flex",gap:8}}>
                    {[{key:"day",placeholder:"GG",options:Array.from({length:31},function(_,i){return String(i+1).padStart(2,"0");})},{key:"month",placeholder:"MM",options:["01","02","03","04","05","06","07","08","09","10","11","12"]},{key:"year",placeholder:"AAAA",options:Array.from({length:100},function(_,i){return String(new Date().getFullYear()-i);})}].map(function(sel){
                      var val=ob[sel.key]||"";
                      return <select key={sel.key} value={val} onChange={function(e){var v=e.target.value;setOb(function(prev){var n={...prev};n[sel.key]=v;return n;});}}
                        style={{flex:sel.key==="year"?2:1,background:"rgba(255,255,255,0.05)",border:"1px solid "+gold,borderRadius:8,color:val?"#e8d5b7":"#cabaa2",fontSize:14,padding:"12px 8px",outline:"none",fontFamily:"Mulish,sans-serif",colorScheme:"dark",cursor:"pointer",appearance:"none",textAlign:"center"}}>
                        <option value="" disabled style={{color:"#cabaa2"}}>{sel.placeholder}</option>
                        {sel.options.map(function(o){return <option key={o} value={o} style={{background:"#1a0a2e",color:"#e8d5b7"}}>{o}</option>;})}
                      </select>;
                    })}
                  </div>
                </div>
                <div onClick={ob.name.trim()?function(){setOnboardingStep(1);}:undefined}
                  style={{background:ob.name.trim()?"linear-gradient(135deg,"+purple+",#4c1d95)":"#2a1a3e",color:ob.name.trim()?"#fff":"#6a5a7a",border:"1px solid "+(ob.name.trim()?gold:"#3a2a5a"),borderRadius:8,padding:"14px",fontSize:15,cursor:ob.name.trim()?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  Avanti <ChevronRight size={16}/>
                </div>
                <div onClick={completeOnboarding} style={{color:"#cabaa2",cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",gap:4,justifyContent:"center"}}>
                  Salta per ora <ChevronRight size={13}/>
                </div>
              </div>
            )}
            {onboardingStep===1&&(
              <div style={{width:"100%",display:"flex",flexDirection:"column",gap:14}}>
                <p style={{color:"#e8d5b7",fontSize:15,margin:0}}>Ciao {ob.name}! Su cosa vuoi concentrarti?</p>
                <div style={{display:"flex",flexDirection:"column",gap:10,width:"100%"}}>
                  {FOCUS_OPTIONS.map(function(f){
                    var FIcon=f.icon; var sel=ob.focus===f.k;
                    return <div key={f.k} onClick={function(){setOb({...ob,focus:f.k});}}
                      style={{background:sel?"rgba(201,168,76,0.15)":"rgba(30,10,60,0.8)",border:"1px solid "+(sel?gold:"#3a2a5a"),borderRadius:12,padding:"14px 18px",cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:14}}>
                      <FIcon size={22} color={sel?gold:"#6a5a7a"} strokeWidth={1.5}/>
                      <div style={{flex:1,color:sel?gold:"#a09080",fontSize:14}}>{f.l}</div>
                      {sel&&<CheckCircle size={16} color={gold}/>}
                    </div>;
                  })}
                </div>
                {/* Consenso marketing */}
                <div onClick={function(){setMarketingConsent(function(v){return !v;});}}
                  style={{display:"flex",alignItems:"flex-start",gap:12,cursor:"pointer",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,padding:"12px 14px",textAlign:"left"}}>
                  <div style={{width:20,height:20,borderRadius:4,border:"1.5px solid "+(marketingConsent?gold:"#3a2a5a"),background:marketingConsent?"rgba(201,168,76,0.2)":"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",marginTop:1}}>
                    {marketingConsent&&<CheckCircle size={13} color={gold}/>}
                  </div>
                  <span style={{fontSize:12,color:"#8a7a9a",lineHeight:1.5}}>
                    Accetto di ricevere comunicazioni promozionali e offerte esclusive via email. Puoi revocare il consenso in qualsiasi momento.
                  </span>
                </div>

                <div style={{display:"flex",gap:10}}>
                  <div onClick={function(){setOnboardingStep(0);}} style={{flex:1,background:"transparent",color:"#6a5a7a",border:"1px solid #3a2a5a",borderRadius:8,padding:"12px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                    <ChevronLeft size={16}/> Indietro
                  </div>
                  <div onClick={ob.focus?completeOnboarding:undefined}
                    style={{flex:2,background:ob.focus?"linear-gradient(135deg,"+gold+",#a07830)":"#2a1a3e",color:ob.focus?"#1a0a2e":"#6a5a7a",border:"none",borderRadius:8,padding:"12px",fontSize:15,cursor:ob.focus?"pointer":"default",fontWeight:"bold",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                    <Sparkles size={16}/> Inizia
                  </div>
                </div>
                <div onClick={completeOnboarding} style={{color:"#6a5a7a",cursor:"pointer",fontSize:12}}>Salta per ora</div>
              </div>
            )}
          </div>
        </div>
      )}

      {!authLoading&&session&&!showOnboarding&&showMarketingPopup&&(
        <div style={{position:"fixed",inset:0,zIndex:250,background:"rgba(6,4,16,0.93)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{width:"100%",maxWidth:380,background:"linear-gradient(135deg,rgba(20,8,45,0.98),rgba(10,4,25,0.98))",border:"1px solid rgba(201,168,76,0.25)",borderRadius:20,padding:"32px 24px",display:"flex",flexDirection:"column",alignItems:"center",gap:20,textAlign:"center"}}>
            <div style={{width:56,height:56,borderRadius:"50%",background:"rgba(201,168,76,0.12)",border:"1px solid rgba(201,168,76,0.3)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Sparkles size={26} color={gold}/>
            </div>
            <h2 style={{color:gold,fontSize:22,letterSpacing:1,margin:0,lineHeight:1.3}}>Rimani in contatto con le stelle</h2>
            <p style={{color:"#a09080",fontSize:14,margin:0,lineHeight:1.7}}>
              Posso inviarti via email consigli per usare l'app, contenuti inerenti ai tarocchi, aggiornamenti e informazioni sulla Lettura Combinata? Puoi cancellarti in qualsiasi momento.
            </p>
            <div style={{width:"100%",display:"flex",flexDirection:"column",gap:10}}>
              <div onClick={function(){
                localStorage.setItem("rt_marketing_popup_shown","1");
                setShowMarketingPopup(false);
                if(session) saveUserData(session.user.id,{email_marketing_consent:true,email_marketing_consent_at:new Date().toISOString()});
              }} style={{width:"100%",background:"linear-gradient(135deg,rgba(201,168,76,0.25),rgba(160,120,48,0.15))",border:"1px solid "+gold,borderRadius:12,padding:"14px 20px",cursor:"pointer",color:gold,fontSize:15,fontWeight:"bold",letterSpacing:0.5,boxSizing:"border-box",textAlign:"center"}}>
                Sì, voglio riceverle ✨
              </div>
              <div onClick={function(){
                localStorage.setItem("rt_marketing_popup_shown","1");
                setShowMarketingPopup(false);
                if(session) saveUserData(session.user.id,{email_marketing_consent:false,email_marketing_consent_at:new Date().toISOString()});
              }} style={{color:"#6a5a7a",fontSize:13,cursor:"pointer",padding:"8px",textAlign:"center"}}>
                No grazie
              </div>
            </div>
          </div>
        </div>
      )}

      {!authLoading&&session&&(
        <div>
          {tab==="consult"&&screen==="home"&&(
            <div style={ps}><div style={ss}>
              <RosemaryImg/>
              <h1 style={{fontSize:32,color:gold,letterSpacing:3,margin:"0 0 4px",textShadow:"0 0 20px rgba(201,168,76,0.5)"}}>Rosemary Tarot</h1>
              <p style={{color:"#a09080",fontSize:14,letterSpacing:1,margin:0}}>Scegli come vuoi la tua lettura.</p>
              <div style={{display:"flex",flexDirection:"column",gap:10,width:"100%",maxWidth:360,marginTop:8}}>
                <div onClick={function(){
                    if(!canConsult){setTab("consult");setScreen("premium");return;}
                    setDeckMode("full");setScreen("question");
                  }}
                  style={{background:"rgba(30,10,60,0.8)",border:"1px solid "+gold,borderRadius:12,padding:"16px 18px",cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:14}}>
                  <Sparkles size={26} color={gold} strokeWidth={1.5}/>
                  <div style={{flex:1}}>
                    <div style={{color:gold,fontSize:16,marginBottom:2}}>Lettura AI</div>
                    <div style={{color:"#8a7a9a",fontSize:12}}>Fai la tua domanda, l'IA di Rosemary interpreta le carte all'istante.</div>
                  </div>
                  <ChevronRight size={18} color={gold}/>
                </div>
                <div onClick={function(){setScreen("real"); if(session) loadRealReadings(session.user.id);}}
                  style={{background:"rgba(30,10,60,0.8)",border:"1px solid "+purple,borderRadius:12,padding:"16px 18px",cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:14}}>
                  <DoubleHeart size={26} color="#a090e0" strokeWidth={1.5}/>
                  <div style={{flex:1}}>
                    <div style={{color:"#c9b8f0",fontSize:16,marginBottom:2}}>Lettura combinata</div>
                    <div style={{color:"#8a7a9a",fontSize:12}}>Una lettura personale registrata da Rosemary in persona per rispondere alla tua domanda.</div>
                  </div>
                  <ChevronRight size={18} color="#a090e0"/>
                </div>
              </div>
              {!data.isPremium&&<p style={{fontSize:12,color:"#6a5a7a",margin:0}}>{canUseFree?"Oggi hai 1 lettura AI gratuita.":"Lettura AI gratuita esaurita • Passa a Premium"}</p>}
              {data.isPremium&&<p style={{fontSize:12,color:gold,margin:0,display:"flex",alignItems:"center",gap:6}}><Crown size={12}/> Account Premium attivo</p>}
            </div></div>
          )}

          {tab==="consult"&&screen==="real"&&(
            <div style={ps}><div style={ss}>
              <DoubleHeart size={40} color="#a090e0" strokeWidth={1}/>
              <h2 style={{color:"#c9b8f0",fontSize:26,letterSpacing:2,margin:0}}>Lettura combinata</h2>
              <p style={{color:"#a09080",fontSize:14,lineHeight:1.6,maxWidth:340}}>
                Una lettura personale con due mazzi distinti, registrata da una vera cartomante.
                La riceverai direttamente qui nell'app.
              </p>

              {realOrderSuccess&&(
                <div style={{background:"rgba(124,58,237,0.15)",border:"1px solid rgba(124,58,237,0.5)",borderRadius:8,padding:"10px 16px",maxWidth:340}}>
                  <p style={{color:"#c9b8f0",fontSize:13,margin:0}}>Ordine ricevuto! Riceverai la tua lettura appena sarà pronta.</p>
                </div>
              )}

              <div style={{width:"100%",maxWidth:340,display:"flex",flexDirection:"column",gap:10}}>
                <textarea value={realQuestion} onChange={function(e){setRealQuestion(e.target.value);}}
                  maxLength={500} placeholder="Scrivi la tua domanda per la cartomante…"
                  style={{width:"100%",minHeight:90,background:"rgba(255,255,255,0.05)",border:"1px solid "+purple,borderRadius:8,color:"#e8d5b7",fontSize:14,padding:14,resize:"none",outline:"none",fontFamily:"Mulish,sans-serif",boxSizing:"border-box"}}/>
                {data.isPremium
                  ? <p style={{fontSize:12,color:gold,margin:0,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Crown size={12}/> Prezzo Premium: 24,90 €</p>
                  : <p style={{fontSize:12,color:"#8a7a9a",margin:0,textAlign:"center"}}>Prezzo: 29 € — <span style={{color:gold}}>con Premium 24,90 €</span></p>}
                <div onClick={(loadingRealCheckout||realQuestion.trim().length<3)?undefined:startRealReadingCheckout}
                  style={{background:(realQuestion.trim().length<3)?"rgba(60,40,100,0.4)":"linear-gradient(135deg,"+purple+",#4c1d95)",color:"#fff",border:"1px solid "+((realQuestion.trim().length<3)?"rgba(201,168,76,0.2)":gold),borderRadius:8,padding:"13px",cursor:(loadingRealCheckout||realQuestion.trim().length<3)?"default":"pointer",fontWeight:"bold",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  {loadingRealCheckout?<><Clock size={16}/> Reindirizzo…</>:<><DoubleHeart size={16} color="#fff"/> Ordina — {data.isPremium?"24,90 €":"29 €"}</>}
                </div>
              </div>

              {realReadings.filter(function(r){return r.status!=="pending_payment";}).length>0&&(
                <div style={{width:"100%",maxWidth:340,marginTop:8}}>
                  <h3 style={{color:gold,fontSize:15,letterSpacing:1,margin:"0 0 10px",textAlign:"left"}}>Le mie letture combinate</h3>
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {realReadings.filter(function(r){return r.status!=="pending_payment";}).map(function(r){
                      var delivered = r.status==="delivered" && r.media_url;
                      return (
                        <div key={r.id} style={{background:"rgba(30,10,60,0.6)",border:"1px solid "+(delivered?gold:"#3a2a5a"),borderRadius:10,padding:14,textAlign:"left"}}>
                          <div style={{fontSize:11,color:"#6a5a7a",marginBottom:4}}>{new Date(r.created_at).toLocaleDateString("it-IT")}</div>
                          <div style={{fontSize:13,color:"#c8b89a",marginBottom:8}}>{r.question}</div>
                          {delivered?(
                            <div onClick={function(){ if(window.Capacitor&&window.Capacitor.isNativePlatform()){Browser.open({url:r.media_url});}else{window.open(r.media_url,"_blank");} }}
                              style={{background:"linear-gradient(135deg,"+gold+",#a07830)",color:"#1a0a2e",borderRadius:8,padding:"10px",cursor:"pointer",fontWeight:"bold",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                              <Video size={15}/> Guarda la tua lettura
                            </div>
                          ):(
                            <div style={{fontSize:12,color:"#a090e0",display:"flex",alignItems:"center",gap:6}}><Clock size={13}/> In lavorazione</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div onClick={function(){setScreen("home");}} style={{marginTop:8,color:"#a09080",border:"1px solid #4a3a5a",borderRadius:8,padding:"10px 20px",cursor:"pointer",display:"flex",alignItems:"center",gap:6}}><ChevronLeft size={16}/> Indietro</div>
            </div></div>
          )}

          {tab==="consult"&&screen==="question"&&(
            <div style={ps}><div style={ss}>
              <RosemaryImg img="rosemary-tarot_cards.webp"/>
              <h2 style={{color:gold,fontSize:26,letterSpacing:2,margin:0}}>La tua domanda</h2>
              {invalidQuestion&&(
                <div style={{background:"rgba(180,60,60,0.15)",border:"1px solid rgba(220,80,80,0.4)",borderRadius:8,padding:"10px 16px",maxWidth:340,textAlign:"center"}}>
                  <p style={{color:"#e8a0a0",fontSize:13,margin:0}}>
                    {question.trim() ? "La tua domanda non è chiara per le stelle. Prova a riformularla." : "Non ho sentito nulla. Prova a registrare di nuovo."}
                  </p>
                </div>
              )}
              {showTranscriptEdit?(
                <div style={{width:"100%",maxWidth:340,display:"flex",flexDirection:"column",gap:10}}>
                  <p style={{color:gold,fontSize:12,margin:0,textAlign:"center"}}>Modifica se necessario:</p>
                  <textarea value={question} onChange={function(e){setQuestion(e.target.value);}}
                    style={{width:"100%",minHeight:80,background:"rgba(255,255,255,0.05)",border:"1px solid "+gold,borderRadius:8,color:"#e8d5b7",fontSize:14,padding:14,resize:"none",outline:"none",fontFamily:"Mulish,sans-serif",boxSizing:"border-box"}}/>
                  <div style={{display:"flex",gap:10}}>
                    <div onClick={function(){setShowTranscriptEdit(false);setQuestion("");}} style={{flex:1,background:"transparent",color:"#a09080",border:"1px solid #4a3a5a",borderRadius:8,padding:"10px",cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                      <RotateCcw size={14}/> Riregistra
                    </div>
                    <div onClick={function(){startConsultation(questionRef.current);}}
                      style={{flex:2,background:"linear-gradient(135deg,"+purple+",#4c1d95)",color:"#fff",border:"1px solid "+gold,borderRadius:8,padding:"10px",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                      {validating?<><Clock size={14}/> Verifico…</>:<><Sparkles size={14}/> Consulta</>}
                    </div>
                  </div>
                </div>
              ):(
                <div style={{width:"100%",maxWidth:340,position:"relative"}}>
                  <textarea value={question} onChange={function(e){setQuestion(e.target.value);setInvalidQuestion(false);}}
                    placeholder="Scrivi la tua domanda oppure usa il microfono"
                    style={{width:"100%",minHeight:100,background:"rgba(255,255,255,0.05)",border:"1px solid "+(recording?"#e05050":gold),borderRadius:8,color:"#e8d5b7",fontSize:14,padding:"14px 52px 14px 14px",resize:"none",outline:"none",fontFamily:"Mulish,sans-serif",boxSizing:"border-box"}}/>
                  <div onClick={recording?stopRecording:startRecording}
                    style={{position:"absolute",right:10,bottom:10,width:36,height:36,borderRadius:"50%",border:"1px solid "+(recording?"#e05050":gold),background:recording?"rgba(220,50,50,0.2)":"rgba(201,168,76,0.1)",color:recording?"#e05050":gold,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",animation:recording?"pulse 1s infinite":"none"}}>
                    {transcribing?<Clock size={16}/>:recording?<Square size={16}/>:<Mic size={16}/>}
                  </div>
                </div>
              )}
              {transcribing&&<p style={{color:gold,fontSize:12,fontStyle:"italic",margin:0,display:"flex",alignItems:"center",gap:6}}><Clock size={12}/> Trascrizione in corso…</p>}
              {recording&&(
                <div style={{textAlign:"center"}}>
                  <p style={{color:"#e05050",fontSize:12,margin:"0 0 4px",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><MicOff size={12}/> Registrazione in corso…</p>
                  <p style={{color:"#8a7a9a",fontSize:11,margin:0,fontStyle:"italic"}}>Quando hai finito di parlare, premi il tasto per fermare ✦</p>
                </div>
              )}
              {!showTranscriptEdit&&(
                <div style={{display:"flex",gap:12}}>
                  <div onClick={function(){setScreen("home");}} style={{background:"transparent",color:"#a09080",border:"1px solid #4a3a5a",borderRadius:8,padding:"10px 20px",cursor:"pointer",display:"flex",alignItems:"center",gap:6}}><ChevronLeft size={16}/> Indietro</div>
                  <div onClick={question.trim()&&!validating&&!recording&&!transcribing?function(){startConsultation();}:undefined}
                    style={{background:question.trim()?"linear-gradient(135deg,"+purple+",#4c1d95)":"#2a1a3e",color:question.trim()?"#fff":"#6a5a7a",border:"1px solid "+(question.trim()?gold:"#3a2a5a"),borderRadius:8,padding:"10px 24px",cursor:question.trim()?"pointer":"default",fontSize:15,display:"flex",alignItems:"center",gap:8}}>
                    {validating?<><Clock size={16}/> Verifico…</>:<><Sparkles size={16}/> Consulta</>}
                  </div>
                </div>
              )}
            </div></div>
          )}

          {tab==="consult"&&screen==="drawing"&&(
            <div style={ps}><div style={{...ss,justifyContent:"flex-start",paddingTop:32}}>
              <RosemaryImg img="rosemary-tarot_cards.webp"/>
              <h2 style={{color:gold,fontSize:22,letterSpacing:2,margin:0}}>Le tue carte</h2>
              <p style={{color:"#8a7a9a",fontSize:13,fontStyle:"italic",maxWidth:300,margin:0}}>"{question}"</p>
              <p style={{color:"#a09080",fontSize:14,margin:0}}>
                {!flipped[0]?"Tocca la prima carta":!flipped[1]?"Rivela la seconda":!flipped[2]?"Rivela l'ultima":loadingNarrative?"Rosemary sta leggendo…":"La tua lettura è pronta"}
              </p>
              <div style={{display:"flex",gap:10,justifyContent:"center",width:"100%",maxWidth:360}}>
                {drawnCards.map(function(card,i){
                  return <CardPlaceholder key={i} card={card} flipped={flipped[i]} reversed={card.reversed} position={POSITIONS[i]}
                    onClick={!flipped[i]?function(){flipCard(i);}:function(){setSelectedCard(selectedCard===i?null:i);}}/>;
                })}
              </div>
              {selectedCard!==null&&flipped[selectedCard]&&(
                <div style={{background:"rgba(30,10,60,0.9)",border:"1px solid "+gold,borderRadius:12,padding:16,maxWidth:340,textAlign:"left",animation:"fadeIn 0.4s ease",width:"100%"}}>
                  <h3 style={{color:gold,margin:"0 0 4px",fontSize:15}}>{drawnCards[selectedCard].name}</h3>
                  <div style={{fontSize:10,color:"#8a7a9a",marginBottom:6,textTransform:"uppercase",letterSpacing:1}}>{POSITIONS[selectedCard]}</div>
                  <p style={{color:"#c8b89a",fontSize:13,lineHeight:1.6,margin:0}}>{drawnCards[selectedCard].reversed?drawnCards[selectedCard].meaning_rev:drawnCards[selectedCard].meaning_up}</p>
                </div>
              )}
              {allFlipped()&&(loadingNarrative?<LoadingOrb/>:narrative?(
                <div style={{display:"flex",flexDirection:"column",gap:12,alignItems:"center",width:"100%"}}>
                  <div style={{background:"rgba(30,10,60,0.85)",border:"1px solid rgba(201,168,76,0.4)",borderRadius:12,padding:18,maxWidth:380,textAlign:"left",width:"100%"}}>
                    <div style={{fontSize:11,color:gold,letterSpacing:2,textTransform:"uppercase",marginBottom:8,display:"flex",alignItems:"center",gap:6}}><Sparkles size={12}/> La lettura di Rosemary</div>
                    <p style={{color:"#d8c8aa",fontSize:14,lineHeight:1.8,margin:0,fontStyle:"italic"}}>{narrative}</p>
                  </div>
                  <div onClick={function(){setScreen("reading");}}
                    style={{background:"linear-gradient(135deg,"+gold+",#a07830)",color:"#1a0a2e",border:"none",borderRadius:8,padding:"12px 28px",fontSize:15,cursor:"pointer",fontWeight:"bold",display:"flex",alignItems:"center",gap:8}}>
                    Vedi il riepilogo completo <ChevronRight size={16}/>
                  </div>
                </div>
              ):null)}
            </div></div>
          )}

          {tab==="consult"&&screen==="reading"&&(
            <div style={ps}><div style={{paddingTop:32}}>
              <div style={{textAlign:"center",marginBottom:20}}>
                <RosemaryImg/>
                <h2 style={{color:gold,letterSpacing:2,margin:"12px 0 4px",fontSize:26}}>La tua Lettura</h2>
                <p style={{color:"#8a7a9a",fontSize:13,fontStyle:"italic"}}>"{question}"</p>
              </div>
              {narrative&&(
                <div style={{background:"rgba(30,10,60,0.85)",border:"1px solid "+gold,borderRadius:12,padding:20,marginBottom:16}}>
                  <div style={{fontSize:11,color:gold,letterSpacing:2,textTransform:"uppercase",marginBottom:10,display:"flex",alignItems:"center",gap:6}}><Sparkles size={12}/> La lettura di Rosemary</div>
                  <p style={{color:"#d8c8aa",fontSize:14,lineHeight:1.8,margin:0,fontStyle:"italic"}}>{narrative}</p>
                </div>
              )}
              {drawnCards.map(function(card,i){
                return <div key={i} style={{background:"rgba(30,10,60,0.7)",border:"1px solid rgba(201,168,76,0.3)",borderRadius:12,padding:16,marginBottom:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:14}}>
                    <CardPlaceholder card={card} flipped={true} reversed={card.reversed} small={true}/>
                    <div style={{flex:1}}>
                      <div style={{fontSize:10,color:gold,letterSpacing:2,textTransform:"uppercase"}}>{POSITIONS[i]}</div>
                      <div style={{fontSize:16,color:"#e8d5b7",margin:"4px 0"}}>{card.name}</div>
                      <p style={{fontSize:12,color:"#a09080",lineHeight:1.6,margin:0}}>{card.reversed?card.meaning_rev:card.meaning_up}</p>
                    </div>
                  </div>
                </div>;
              })}
              <div style={{display:"flex",gap:12,justifyContent:"center",marginTop:20,flexWrap:"wrap"}}>
                <div onClick={function(){setScreen("home");setQuestion("");setNarrative("");setShowTranscriptEdit(false);}}
                  style={{background:"linear-gradient(135deg,"+purple+",#4c1d95)",color:"#fff",border:"1px solid "+gold,borderRadius:8,padding:"12px 22px",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",gap:8}}>
                  <Sparkles size={16}/> Nuova Lettura
                </div>
                <div onClick={function(){setScreen("home");}}
                  style={{background:"transparent",color:gold,border:"1px solid "+gold,borderRadius:8,padding:"12px 22px",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",gap:8}}>
                  <ChevronLeft size={16}/> Home
                </div>
              </div>
            </div></div>
          )}

          {tab==="consult"&&screen==="premium"&&(
            <div style={ps}><div style={ss}>
              <RosemaryImg/>
              <h2 style={{color:gold,fontSize:24,letterSpacing:2,margin:0}}>Rosemary Premium</h2>
              <p style={{color:"#a09080",fontSize:13,maxWidth:300,textAlign:"center"}}>Prova 7 giorni gratis. Disdici quando vuoi.</p>
              <div style={{display:"flex",flexDirection:"column",gap:14,width:"100%",maxWidth:320}}>
                <div style={{background:"rgba(30,10,60,0.6)",border:"1px solid #3a2a5a",borderRadius:12,padding:18}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <div style={{fontSize:17,color:"#a09080"}}>Free</div>
                    <div style={{fontSize:22,color:"#a09080",fontWeight:"bold"}}>€0</div>
                  </div>
                  {["1 lettura AI al giorno","Un percorso completo, una volta","Carta del giorno","Lettura combinata a 29 €"].map(function(f,j){
                    return <div key={j} style={{fontSize:12,color:"#6a5a7a",marginBottom:4,display:"flex",alignItems:"center",gap:6}}>
                      <div style={{width:4,height:4,borderRadius:"50%",background:"#6a5a7a",flexShrink:0}}/>
                      {f}
                    </div>;
                  })}
                </div>
                <div style={{background:"rgba(124,58,237,0.15)",border:"2px solid "+gold,borderRadius:12,padding:18,position:"relative"}}>
                  <div style={{position:"absolute",top:-10,left:"50%",transform:"translateX(-50%)",background:gold,color:"#1a0a2e",fontSize:10,fontWeight:"bold",padding:"3px 12px",borderRadius:20,letterSpacing:1,whiteSpace:"nowrap"}}>
                    CONSIGLIATO
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                    <div style={{fontSize:17,color:gold}}>Premium</div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:15,color:gold,fontWeight:"bold"}}>7 giorni gratis</div>
                      <div style={{fontSize:12,color:"#a09080"}}>poi €4,99/mese o €39,99/anno</div>
                    </div>
                  </div>
                  {["Letture AI illimitate","Percorsi illimitati","Lettura combinata sempre a 24,90 € (invece di 29 €)","Carta del giorno e storico completo"].map(function(f,j){
                    return <div key={j} style={{fontSize:12,color:"#c8b89a",marginBottom:4,display:"flex",alignItems:"center",gap:6}}>
                      <CheckCircle size={11} color={gold} style={{flexShrink:0}}/>
                      {f}
                    </div>;
                  })}
                  <div onClick={loadingCheckout?undefined:function(){activatePremium("monthly");}}
                    style={{marginTop:14,background:loadingCheckout==="monthly"?"#2a1a3e":"linear-gradient(135deg,"+gold+",#a07830)",color:loadingCheckout==="monthly"?"#6a5a7a":"#1a0a2e",borderRadius:8,padding:"13px",cursor:loadingCheckout?"default":"pointer",fontWeight:"bold",fontSize:14,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2,transition:"all 0.2s"}}>
                    {loadingCheckout==="monthly"?<div style={{display:"flex",alignItems:"center",gap:8}}><Clock size={16}/> Reindirizzo…</div>:<><div style={{display:"flex",alignItems:"center",gap:8}}><Crown size={16}/> Inizia 7 giorni gratis</div><div style={{fontSize:11,fontWeight:"normal",opacity:0.85}}>poi €4,99/mese, disdici quando vuoi</div></>}
                  </div>
                  <div onClick={loadingCheckout?undefined:function(){activatePremium("yearly");}}
                    style={{marginTop:8,background:loadingCheckout==="yearly"?"#2a1a3e":"rgba(201,168,76,0.12)",color:loadingCheckout==="yearly"?"#6a5a7a":gold,border:"1px solid "+gold,borderRadius:8,padding:"13px",cursor:loadingCheckout?"default":"pointer",fontWeight:"bold",fontSize:14,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2,transition:"all 0.2s"}}>
                    {loadingCheckout==="yearly"?<div style={{display:"flex",alignItems:"center",gap:8}}><Clock size={16}/> Reindirizzo…</div>:<><div style={{display:"flex",alignItems:"center",gap:8}}><Crown size={16}/> Annuale — €39,99/anno</div><div style={{fontSize:11,fontWeight:"normal",opacity:0.85}}>7 giorni gratis, poi ~3,33 €/mese · risparmi il 33%</div></>}
                  </div>
                </div>
              </div>
              <div onClick={function(){setScreen("home");}} style={{color:"#6a5a7a",cursor:"pointer",fontSize:13}}>Continua gratuitamente</div>
            </div></div>
          )}

          {tab==="daily"&&(
            <div style={ps}><div style={ss}>
              <RosemaryImg/>
              <h2 style={{color:gold,fontSize:26,letterSpacing:2,margin:0}}>Carta del Giorno</h2>
              <p style={{color:"#a09080",fontSize:14,margin:0}}>Una carta per osservare meglio la tua giornata.</p>
              <div style={{width:140,margin:"8px auto",cursor:dailyRevealed?"default":"pointer"}} onClick={!dailyRevealed?revealDailyCard:undefined}>
                <div style={{width:140,aspectRatio:"2/3.4",borderRadius:12,border:"2px solid "+(dailyRevealed?gold:"rgba(201,168,76,0.5)"),background:dailyRevealed?"linear-gradient(135deg,#2a1a4e,#1a0a2e)":"linear-gradient(135deg,#4a2a8e,#2a1a6e)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:dailyRevealed?"0 0 30px rgba(201,168,76,0.4)":"0 0 20px rgba(124,58,237,0.4)",transition:"all 0.6s ease",position:"relative",overflow:"hidden",animation:!dailyRevealed?"cardPulse 2s ease-in-out infinite":"none"}}>
                  {dailyRevealed&&dailyCard
                    ?<img src={"/cards/"+dailyCard.img} alt={dailyCard.name} className="card-flip" style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:10}}/>
                    :<><div style={{textAlign:"center",padding:16}}><Sun size={32} color="rgba(201,168,76,0.6)" strokeWidth={1}/><p style={{color:"rgba(201,168,76,0.5)",fontSize:11,margin:"8px 0 0",letterSpacing:2,textTransform:"uppercase"}}>Tocca per rivelare</p></div><div style={{position:"absolute",inset:0,background:"radial-gradient(circle at 50% 40%,rgba(124,58,237,0.2),transparent 70%)",pointerEvents:"none"}}/></>
                  }
                </div>
              </div>
              {loadingDaily&&<LoadingOrb/>}
              {dailyCard&&!loadingDaily&&(
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:12,width:"100%",animation:"fadeIn 0.6s ease"}}>
                  <p style={{color:gold,fontSize:15,margin:0,fontStyle:"italic"}}>{dailyCard.name}{dailyCard.reversed?" — Rovesciata":""}</p>
                  {dailyNarrative&&(
                    <div style={{background:"rgba(30,10,60,0.85)",border:"1px solid rgba(201,168,76,0.4)",borderRadius:12,padding:18,maxWidth:380,textAlign:"left",width:"100%"}}>
                      <div style={{fontSize:11,color:gold,letterSpacing:2,textTransform:"uppercase",marginBottom:8,display:"flex",alignItems:"center",gap:6}}><Sun size={12}/> Il messaggio di oggi</div>
                      <p style={{color:"#d8c8aa",fontSize:14,lineHeight:1.8,margin:0,fontStyle:"italic"}}>{dailyNarrative}</p>
                    </div>
                  )}
                  <p style={{fontSize:12,color:"#6a5a7a",margin:0,display:"flex",alignItems:"center",gap:6}}><Sun size={12}/> Torna domani per una nuova carta</p>
                </div>
              )}
            </div></div>
          )}

          {tab==="streak"&&(
            <div style={ps}><div style={{...ss,justifyContent:"flex-start",paddingTop:32}}>
              <RosemaryImg/>
              <h2 style={{color:gold,fontSize:26,letterSpacing:2,margin:0}}>Percorso</h2>
              <p style={{color:"#a09080",fontSize:13,margin:0,maxWidth:300,textAlign:"center"}}>Scegli una domanda che vuoi osservare nel tempo. Ogni giorno una carta ti aiuta a leggerne l'evoluzione.</p>

              {/* Journey question display (locked) */}
              {journeyQuestion && (
                <div style={{width:"100%",maxWidth:360,background:"rgba(201,168,76,0.08)",border:"1px solid rgba(201,168,76,0.3)",borderRadius:12,padding:"14px 16px"}}>
                  <div style={{fontSize:10,color:gold,letterSpacing:2,textTransform:"uppercase",marginBottom:6,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span>La tua domanda</span>
                    <Lock size={12} color="#3a2a5a"/>
                  </div>
                  <p style={{color:"#d8c8aa",fontSize:13,fontStyle:"italic",margin:0,lineHeight:1.5}}>"{journeyQuestion}"</p>
                </div>
              )}

              {/* Start journey button if no question */}
              {!journeyQuestion && (
                <div onClick={function(){ if(!canStartNewJourney){setTab("consult");setScreen("premium");return;} setShowJourneySetup(true); }}
                  style={{background:"linear-gradient(135deg,"+purple+",#4c1d95)",color:"#fff",border:"1px solid "+gold,borderRadius:12,padding:"14px 24px",cursor:"pointer",fontSize:15,display:"flex",alignItems:"center",gap:10}}>
                  {canStartNewJourney ? <><Sparkles size={16}/> Avvia il tuo Percorso</> : <><Crown size={16}/> Nuovo percorso • Premium</>}
                </div>
              )}

              {/* Days counter */}
              <div style={{display:"flex",alignItems:"center",gap:16}}>
                <Moon size={32} color={gold} strokeWidth={1}/>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:42,color:gold,fontWeight:"bold",fontFamily:"Cormorant Garamond,serif",lineHeight:1}}>{Object.keys(journeyOpenedDates).length}</div>
                  <div style={{fontSize:12,color:"#a09080"}}>giorni di percorso</div>
                </div>
                <Moon size={32} color={gold} strokeWidth={1} style={{transform:"scaleX(-1)"}}/>
              </div>

              {/* Journey stages */}
              <div style={{display:"flex",flexDirection:"column",gap:10,width:"100%",maxWidth:360}}>
                {LUNAR_STAGES.map(function(stage, idx){
                  var achieved = isStageUnlocked(stage, idx);
                  var locked = false;
                  var isActive = activeJourneyStage === stage.days;
                  var hasReading = !!journeyReadings[stage.days];
                  var isLoading = !!loadingJourneyStage[stage.days];
                  var isNext = idx === currentStageIndex;
                  var availableTomorrow = !achieved && idx > 0 && journeyOpenedDates[LUNAR_STAGES[idx-1].days] === todayISO;

                  return (
                    <div key={stage.days}>
                      <div onClick={achieved && !locked && journeyQuestion ? function(){unlockJourneyStage(stage);} : locked ? function(){setTab("consult");setScreen("premium");} : undefined}
                        style={{background: achieved ? "rgba(201,168,76,0.1)" : isNext ? "rgba(124,58,237,0.1)" : "rgba(30,10,60,0.6)", border: "1px solid " + (achieved ? gold : isNext ? purple : "#3a2a5a"), borderRadius:10, padding:"14px 16px", display:"flex", alignItems:"center", gap:14, opacity: achieved||isNext ? 1 : 0.5, cursor: achieved && !locked && journeyQuestion ? "pointer" : locked ? "pointer" : "default", transition:"all 0.3s ease"}}>
                        <div style={{width:28,height:28,borderRadius:"50%",border:"1.5px solid "+(achieved?gold:isNext?purple:"#3a2a5a"),display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:achieved?gold:isNext?"#7c3aed":"#3a2a5a",fontWeight:600,flexShrink:0}}>
                          {idx+1}
                        </div>
                        <div style={{flex:1}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                            <span style={{color: achieved ? gold : isNext ? "#a090e0" : "#4a3a6a", fontSize:14, fontWeight:600}}>{stage.name}</span>
                            {locked && <span style={{fontSize:10,color:gold,background:"rgba(201,168,76,0.15)",padding:"2px 8px",borderRadius:10}}>Premium</span>}
                            {hasReading && <CheckCircle size={13} color={gold}/>}
                          </div>
                          <div style={{color:"#8a7a9a",fontSize:12,marginBottom:availableTomorrow?4:0}}>{stage.subtitle}</div>
                          {availableTomorrow && <div style={{color:"#7c3aed",fontSize:11}}>disponibile domani</div>}
                          {achieved && !journeyQuestion && <div style={{color:"#8a7a9a",fontSize:11,marginTop:3}}>Scrivi prima la tua domanda</div>}
                        </div>
                        {locked ? <Lock size={16} color={gold}/> : achieved ? (isActive ? <ChevronLeft size={16} color={gold}/> : <ChevronRight size={16} color={gold}/>) : null}
                      </div>

                      {/* Expandable narrative */}
                      {isActive && (
                        <div style={{background:"rgba(20,8,50,0.95)",border:"1px solid rgba(201,168,76,0.25)",borderRadius:10,padding:18,marginTop:4,animation:"fadeIn 0.4s ease"}}>
                          {isLoading ? <LoadingOrb/> : (
                            <>
                              <div style={{fontSize:10,color:gold,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>
                                {stage.name} — {stage.subtitle}
                              </div>
                              <p style={{color:"#d8c8aa",fontSize:13,lineHeight:1.9,margin:0,fontStyle:"italic"}}>{journeyReadings[stage.days]}</p>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Restart button after all 7 days opened */}
              {Object.keys(journeyOpenedDates).length >= LUNAR_STAGES.length && (
                <div onClick={function(){ if(!canStartNewJourney){setTab("consult");setScreen("premium");return;} resetJourney(); }}
                  style={{marginTop:8,background:"rgba(30,10,60,0.8)",border:"1px solid rgba(201,168,76,0.4)",borderRadius:12,padding:"14px 24px",cursor:"pointer",fontSize:14,color:gold,display:"flex",alignItems:"center",gap:10,justifyContent:"center"}}>
                  {canStartNewJourney ? <><RotateCcw size={15} color={gold}/> Avvia nuovo percorso</> : <><Crown size={15} color={gold}/> Nuovo percorso • Passa a Premium</>}
                </div>
              )}

            </div></div>
          )}

          {/* Journey setup popup */}
          {showJourneySetup && (
            <div style={{position:"fixed",inset:0,background:"rgba(10,0,30,0.92)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
              <div style={{background:"linear-gradient(135deg,#1a0a3e,#0d0620)",border:"1px solid rgba(201,168,76,0.5)",borderRadius:16,padding:28,maxWidth:400,width:"100%",display:"flex",flexDirection:"column",gap:18}}>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:32,color:gold,fontFamily:"Cormorant Garamond,serif",marginBottom:6}}>Il tuo Percorso</div>
                  <p style={{color:"#a09080",fontSize:13,margin:0,lineHeight:1.6}}>Scegli una domanda da esplorare nei prossimi 7 giorni. Non potrai cambiarla fino alla fine del percorso.</p>
                </div>
                <textarea
                  value={journeyQuestionDraft}
                  onChange={function(e){setJourneyQuestionDraft(e.target.value);}}
                  placeholder="Es: Come posso trovare più equilibrio nella mia vita?"
                  style={{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(201,168,76,0.4)",borderRadius:10,color:"#e8d5b7",fontSize:14,padding:14,resize:"none",outline:"none",fontFamily:"Mulish,sans-serif",minHeight:100,boxSizing:"border-box",width:"100%",lineHeight:1.6}}
                />
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  <div
                    onClick={journeyQuestionDraft.trim() ? function(){saveJourneyQuestion(journeyQuestionDraft);setShowJourneySetup(false);} : undefined}
                    style={{background: journeyQuestionDraft.trim() ? "linear-gradient(135deg,"+purple+",#4c1d95)" : "rgba(60,40,100,0.4)",color:"#fff",border:"1px solid "+(journeyQuestionDraft.trim()?gold:"rgba(201,168,76,0.2)"),borderRadius:10,padding:"13px",cursor:journeyQuestionDraft.trim()?"pointer":"default",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",gap:8,transition:"all 0.2s"}}>
                    <Sparkles size={15}/> Avvia Percorso
                  </div>
                  <div onClick={function(){setShowJourneySetup(false);}} style={{color:"#6a5a7a",cursor:"pointer",fontSize:13,textAlign:"center"}}>Annulla</div>
                </div>
              </div>
            </div>
          )}

          {tab==="profile"&&(
            <div style={ps}><div style={ss}>
              <RosemaryImg/>
              <h2 style={{color:gold,fontSize:26,letterSpacing:2,margin:0}}>{data.userName?"Ciao, "+data.userName:"Profilo"}</h2>
              <div style={{background:"rgba(30,10,60,0.8)",border:"1px solid rgba(201,168,76,0.3)",borderRadius:12,padding:20,width:"100%",maxWidth:340}}>
                {data.userName?(
                  <div style={{marginBottom:16,paddingBottom:16,borderBottom:"1px solid rgba(201,168,76,0.2)"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                      <span style={{color:gold,fontSize:13,letterSpacing:1}}>I tuoi dati</span>
                      <div onClick={function(){setShowOnboarding(true);}} style={{color:"#6a5a7a",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",gap:4}}>
                        <RotateCcw size={11}/> Modifica
                      </div>
                    </div>
                    {data.userFocus&&<div style={{fontSize:12,color:"#a09080"}}>Focus: <span style={{color:"#e8d5b7"}}>{data.userFocus}</span></div>}
                    {data.userBirthdate&&<div style={{fontSize:12,color:"#a09080",marginTop:4}}>Nato/a il: <span style={{color:"#e8d5b7"}}>{new Date(data.userBirthdate).toLocaleDateString("it-IT")}</span></div>}
                  </div>
                ):(
                  <div style={{marginBottom:16,paddingBottom:16,borderBottom:"1px solid rgba(201,168,76,0.2)"}}>
                    <p style={{color:"#6a5a7a",fontSize:13,margin:"0 0 10px"}}>Personalizza la tua esperienza con Rosemary</p>
                    <div onClick={function(){setShowOnboarding(true);}}
                      style={{background:"rgba(201,168,76,0.1)",border:"1px solid rgba(201,168,76,0.4)",borderRadius:8,padding:"10px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:10,color:gold,fontSize:13}}>
                      <User size={16}/> Compila il tuo profilo
                      <ChevronRight size={14} style={{marginLeft:"auto"}}/>
                    </div>
                  </div>
                )}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <span style={{color:"#a09080",fontSize:14}}>Piano attuale</span>
                  <span style={{color:gold,fontSize:14,fontWeight:600,display:"flex",alignItems:"center",gap:6}}>{data.isPremium?<><Crown size={14}/> Premium</>:<><User size={14}/> Gratuito</>}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <span style={{color:"#a09080",fontSize:14}}>Consultazioni</span>
                  <span style={{color:"#e8d5b7",fontSize:14,display:"flex",alignItems:"center",gap:6}}><BarChart2 size={14} color={gold}/> {data.consultations.length}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{color:"#a09080",fontSize:14}}>Percorso</span>
                  <span style={{color:"#e8d5b7",fontSize:14,display:"flex",alignItems:"center",gap:6}}><Moon size={14} color={gold}/> {Object.keys(journeyOpenedDates).length} giorni</span>
                </div>
              </div>
              {!data.isPremium&&(
                <div onClick={function(){setTab("consult");setScreen("premium");}}
                  style={{background:"linear-gradient(135deg,"+gold+",#a07830)",color:"#1a0a2e",borderRadius:8,padding:"12px 28px",fontSize:15,cursor:"pointer",fontWeight:"bold",display:"flex",alignItems:"center",gap:8}}>
                  <Crown size={18}/> Passa a Premium
                </div>
              )}
              {data.isPremium&&(
                <div onClick={loadingPortal?undefined:openStripePortal}
                  style={{background:"transparent",border:"1px solid #3a2a5a",borderRadius:8,padding:"12px 28px",fontSize:14,cursor:loadingPortal?"default":"pointer",color:"#6a5a7a",display:"flex",alignItems:"center",gap:8}}>
                  {loadingPortal?<><Clock size={15}/> Apertura portale…</>:<><RotateCcw size={15}/> Gestisci abbonamento</>}
                </div>
              )}
              <div style={{width:"100%",maxWidth:340,boxSizing:"border-box"}}>
                <h3 style={{color:gold,fontSize:16,marginBottom:12,display:"flex",alignItems:"center",gap:8}}><Clock size={16}/> Storico Letture</h3>
                {data.consultations.length===0?(
                  <p style={{color:"#6a5a7a",fontSize:14}}>Nessuna consultazione ancora.</p>
                ):data.consultations.map(function(c,i){
                  return <div key={i} style={{background:"rgba(30,10,60,0.7)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:10,padding:14,marginBottom:10,width:"100%",boxSizing:"border-box"}}>
                    <div style={{fontSize:11,color:"#6a5a7a",marginBottom:4}}>{c.date}</div>
                    <div style={{fontSize:13,color:"#c8b89a",fontStyle:"italic",marginBottom:8}}>"{c.question}"</div>
                    <div style={{display:"flex",gap:6,marginBottom:c.narrative?8:0,overflow:"hidden"}}>
                      {c.cards.map(function(card,j){
                        return <div key={j} style={{flex:1,background:"rgba(255,255,255,0.04)",borderRadius:6,padding:"6px",textAlign:"center"}}>
                          <div style={{fontSize:9,color:gold,marginBottom:2}}>{POSITIONS[j]}</div>
                          <div style={{fontSize:11,color:"#e8d5b7"}}>{card.name}</div>
                        </div>;
                      })}
                    </div>
                    {c.narrative&&(
                      <div onClick={function(){setExpandedConsultation(expandedConsultation===i?null:i);}}
                        style={{background:"transparent",border:"1px solid rgba(201,168,76,0.3)",borderRadius:6,padding:"6px 12px",color:gold,fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",gap:6,justifyContent:"center"}}>
                        {expandedConsultation===i?<><ChevronLeft size={12}/> Chiudi</>:<><Sparkles size={12}/> Leggi la lettura</>}
                      </div>
                    )}
                    {expandedConsultation===i&&c.narrative&&(
                      <div style={{marginTop:10,padding:12,background:"rgba(201,168,76,0.05)",borderRadius:8,animation:"fadeIn 0.3s ease"}}>
                        <p style={{color:"#d8c8aa",fontSize:13,lineHeight:1.8,margin:0,fontStyle:"italic"}}>{c.narrative}</p>
                      </div>
                    )}
                  </div>;
                })}
              </div>
              <div onClick={signOut} style={{background:"transparent",border:"1px solid #3a2a5a",borderRadius:8,padding:"10px 24px",color:"#6a5a7a",cursor:"pointer",fontSize:13}}>
                Esci dall'account
              </div>

              {/* User ID e elimina account */}
              {session&&(
                <div style={{width:"100%",maxWidth:340,display:"flex",flexDirection:"column",gap:8,alignItems:"center"}}>
                  <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:8,padding:"10px 14px",width:"100%"}}>
                    <div style={{fontSize:10,color:"#4a3a6a",letterSpacing:1,textTransform:"uppercase",marginBottom:4}}>Il tuo User ID</div>
                    <div style={{fontSize:11,color:"#4a3a6a",fontFamily:"monospace",wordBreak:"break-all"}}>{session.user.id}</div>
                  </div>
                  <div onClick={function(){
                    var deleteUrl = "https://app.rosemarytarot.com/delete-account?uid="+session.user.id;
                    if(window.Capacitor&&window.Capacitor.isNativePlatform()){
                      Browser.open({url:deleteUrl});
                    } else {
                      window.open(deleteUrl,"_blank");
                    }
                  }} style={{color:"#5a3a3a",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",gap:4}}>
                    Elimina account
                  </div>
                </div>
              )}
            </div></div>
          )}
        </div>
      )}

      {!authLoading&&session&&<BottomNav active={tab} onNav={handleNav} badges={badges}/>}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Mulish:wght@300;400;600&family=Cormorant+Garamond:wght@600;700&display=swap');
        @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
        @keyframes pulse{0%,100%{transform:scale(1);opacity:0.8}50%{transform:scale(1.15);opacity:1}}
        @keyframes cardFlip{0%{transform:perspective(600px) rotateY(90deg) scale(0.8);opacity:0}100%{transform:perspective(600px) rotateY(0deg) scale(1);opacity:1}}
        @keyframes cardPulse{0%,100%{box-shadow:0 0 20px rgba(124,58,237,0.4)}50%{box-shadow:0 0 30px rgba(124,58,237,0.6),0 0 20px rgba(201,168,76,0.15)}}
        @keyframes nebula1{from{transform:scale(1) translate(0,0) rotate(0deg)}to{transform:scale(1.25) translate(3%,5%) rotate(8deg)}}
        @keyframes nebula2{from{transform:scale(1) translate(0,0) rotate(0deg)}to{transform:scale(1.2) translate(-4%,3%) rotate(-6deg)}}
        @keyframes nebula3{from{transform:scale(1) translate(0,0) rotate(0deg)}to{transform:scale(1.15) translate(2%,-4%) rotate(5deg)}}
        @keyframes pulse1{from{opacity:0.6}to{opacity:1}}
        @keyframes pulse2{from{opacity:0.5}to{opacity:0.95}}
        @keyframes pulse3{from{opacity:0.4}to{opacity:0.9}}
        .card-flip{animation:cardFlip 0.5s ease-out forwards;}
        html,body,#root{height:100%;overflow:hidden;position:fixed;width:100%;}
        input[type="email"],input[type="password"],input[type="text"]{-webkit-appearance:none;appearance:none;}
        input::placeholder{color:#cabaa2;}
        textarea::placeholder{color:#4a3a5a;font-family:'Mulish',sans-serif;}
        button:hover{filter:brightness(1.1);}
        h1,h2,h3{font-family:'Cormorant Garamond',Georgia,serif !important;font-weight:700;}
        @supports(height:100dvh){html,body,#root{height:100dvh;}}
        .rt-login-scroll{background:#060410;position:fixed;inset:0;overflow-y:auto;-webkit-overflow-scrolling:touch;}
      `}</style>
    </div>
  );
}